import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantNavigation } from "@/hooks/useTenantNavigation";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Users, MapPin, Wrench, Mountain, Package, Timer, UserPlus, BarChart3, Bell, Send, FileText, FileSpreadsheet, Key, Copy, RefreshCw, Navigation, Building2, Receipt, Calendar, Loader2, Check, X, Mail, DollarSign, Lock, CalendarDays, Filter, AlertCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { NotificationBell } from "@/components/NotificationBell";
import { Textarea } from "@/components/ui/textarea";

// Config for each lookup tab
const TAB_CONFIG = {
  sites: {
    label: "Sites", icon: MapPin, endpoint: "/admin/sites",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "TX - Gonzales" },
      { key: "state", label: "State", type: "select", required: true, options: [
        "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
        "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
        "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
        "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
        "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
      ] },
      { key: "code", label: "Code", type: "text", required: true, placeholder: "TX01" },
    ],
    columns: ["name", "state", "code"],
    supportsTenants: true,
  },
  contractors: {
    label: "Contractors", icon: Users, endpoint: "/admin/contractors",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "code", label: "Code", type: "text", required: true },
      { key: "contract_type", label: "Contract Type", type: "select", options: ["Hourly", "$/cyd", "Hybrid"] },
      { key: "payment_terms", label: "Payment Terms (Days)", type: "select", options: ["15", "30", "45", "60", "90"], placeholder: "Net 30" },
      { key: "billing_email", label: "Billing Email", type: "email", placeholder: "billing@contractor.com" },
    ],
    columns: ["name", "code", "contract_type", "payment_terms", "billing_email"],
    supportsTenants: true,
  },
  equipment: {
    label: "Equipment", icon: Wrench, endpoint: "/admin/equipment",
    fields: [
      { key: "unit_id", label: "Unit ID", type: "text", required: true, placeholder: "CAT336-01" },
      { key: "equipment_type", label: "Type", type: "select", required: true, options: ["Excavator", "Loader", "Dozer", "Grader", "Water Truck", "Haul Truck", "Other"] },
      { key: "make_model", label: "Make/Model", type: "text", placeholder: "CAT 336" },
      { key: "hourly_rate", label: "Hourly Rate ($)", type: "number", placeholder: "150.00" },
      { key: "tare_weight_lb", label: "Default Tare Weight (lb)", type: "number", placeholder: "e.g. 32,500 — leave blank if not a truck" },
    ],
    columns: ["unit_id", "equipment_type", "make_model", "hourly_rate"],
    supportsTenants: true,
  },
  materials: {
    label: "Materials", icon: Mountain, endpoint: "/admin/materials",
    fields: [
      { key: "name", label: "Name", type: "text", required: true },
      { key: "category", label: "Category", type: "select", required: true, options: ["Ore", "Waste", "Other"] },
      { key: "site_id", label: "Mine Site", type: "site_select", required: true },
      { key: "default_po_id", label: "Default Purchase Order (for invoicing)", type: "po_line_select" },
    ],
    columns: ["name", "category", "site_name", "default_po"],
    supportsTenants: true,
  },
  locations: {
    label: "Locations", icon: MapPin, endpoint: "/admin/locations",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "Pit A" },
      { key: "location_type", label: "Type", type: "select", required: true, options: ["Pit/Bench", "ROM", "Stockpile", "WasteDump", "Crusher", "LoadOut", "Other"] },
      { key: "site_id", label: "Site", type: "site_select", required: true },
    ],
    columns: ["name", "location_type", "site_name"],
    supportsTenants: true,
  },
  "delay-codes": {
    label: "Delay Codes", icon: Timer, endpoint: "/admin/delay-codes",
    fields: [
      { key: "code", label: "Code", type: "text", required: true, placeholder: "S1" },
      { key: "category", label: "Category", type: "select", required: true, options: ["Standby", "Downtime"] },
      { key: "description", label: "Description", type: "text", required: true },
    ],
    columns: ["code", "category", "description"],
    supportsTenants: true,
  },
  activities: {
    label: "Activities", icon: Wrench, endpoint: "/admin/activities",
    fields: [
      { key: "name", label: "Name", type: "text", required: true, placeholder: "Loading" },
      { key: "code", label: "Code", type: "text", placeholder: "LOADING" },
      { key: "sort_order", label: "Sort Order", type: "number", placeholder: "0" },
    ],
    columns: ["name", "code", "sort_order"],
    supportsTenants: true,
  },
};

function GeofenceDialog({ site, open, onClose, onSaved }) {
  const [geoType, setGeoType] = useState("polygon");
  const [coordsText, setCoordsText] = useState("");
  const [centerLat, setCenterLat] = useState("");
  const [centerLng, setCenterLng] = useState("");
  const [radius, setRadius] = useState("1");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (site?.geofence) {
      const gf = site.geofence;
      setGeoType(gf.type || "polygon");
      if (gf.type === "polygon" && gf.coordinates) {
        setCoordsText(gf.coordinates.map(c => `${c[0]}, ${c[1]}`).join("\n"));
      }
      if (gf.center) {
        setCenterLat(String(gf.center[0] || ""));
        setCenterLng(String(gf.center[1] || ""));
      }
      setRadius(String(gf.radius_km || 1));
    } else {
      setGeoType("polygon");
      setCoordsText("");
      setCenterLat("");
      setCenterLng("");
      setRadius("1");
    }
  }, [site]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let payload = { type: geoType };
      if (geoType === "polygon") {
        const coords = coordsText.split("\n").filter(l => l.trim()).map(l => {
          const [lat, lng] = l.split(",").map(s => parseFloat(s.trim()));
          if (isNaN(lat) || isNaN(lng)) throw new Error("Invalid coordinate format");
          return [lat, lng];
        });
        if (coords.length < 3) { toast.error("Polygon needs at least 3 coordinate pairs"); setSaving(false); return; }
        payload.coordinates = coords;
      } else {
        if (!centerLat || !centerLng) { toast.error("Center coordinates required"); setSaving(false); return; }
        payload.center = [parseFloat(centerLat), parseFloat(centerLng)];
        payload.radius_km = parseFloat(radius) || 1;
      }
      await api.put(`/admin/sites/${site.id}/geofence`, payload);
      toast.success("Geofence saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message || "Failed to save geofence");
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/admin/sites/${site.id}/geofence`);
      toast.success("Geofence removed");
      onSaved();
      onClose();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader><DialogTitle className="font-headline">Geofence — {site?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Type</Label>
            <Select value={geoType} onValueChange={setGeoType}>
              <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="polygon">Polygon (Perimeter)</SelectItem>
                <SelectItem value="circle">Circle (Center + Radius)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {geoType === "polygon" ? (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Coordinates (one per line: lat, lng)</Label>
              <Textarea
                value={coordsText}
                onChange={e => setCoordsText(e.target.value)}
                placeholder={"29.5710, -97.4437\n29.5730, -97.4410\n29.5715, -97.4385\n29.5690, -97.4420"}
                rows={6}
                className="bg-background border-border font-mono text-xs"
                data-testid="geofence-coords-input"
              />
              <p className="text-xs text-muted-foreground">Enter the mine site perimeter as lat, lng pairs. Minimum 3 points.</p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Center Latitude</Label>
                  <Input value={centerLat} onChange={e => setCenterLat(e.target.value)} placeholder="29.5710" className="bg-background border-border" data-testid="geofence-center-lat" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Center Longitude</Label>
                  <Input value={centerLng} onChange={e => setCenterLng(e.target.value)} placeholder="-97.4437" className="bg-background border-border" data-testid="geofence-center-lng" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Radius (km)</Label>
                <Input value={radius} onChange={e => setRadius(e.target.value)} placeholder="1" className="bg-background border-border" data-testid="geofence-radius" />
              </div>
            </div>
          )}
        </div>
        <DialogFooter className="flex gap-2">
          {site?.geofence && (
            <Button variant="outline" size="sm" onClick={handleRemove} className="border-destructive text-destructive mr-auto" data-testid="remove-geofence-button">Remove</Button>
          )}
          <Button variant="outline" size="sm" onClick={onClose} className="border-border">Cancel</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-geofence-button">{saving ? "Saving..." : "Save Geofence"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LookupTab({ config, globalAdmin }) {
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [sites, setSites] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({});
  const [selectedTenants, setSelectedTenants] = useState([]);
  const [saving, setSaving] = useState(false);
  const [geoSite, setGeoSite] = useState(null);
  const [pos, setPOs] = useState([]);
  const hasPoField = config.fields.some(f => f.type === "po_line_select");

  const loadItems = useCallback(async () => {
    try {
      const [data, tenantData, siteData, poData] = await Promise.all([
        api.get(config.endpoint),
        config.supportsTenants ? api.get("/admin/tenants") : Promise.resolve([]),
        config.fields.some(f => f.type === "site_select") ? api.get("/admin/sites") : Promise.resolve([]),
        hasPoField ? api.get("/admin/purchase-orders").catch(() => []) : Promise.resolve([]),
      ]);
      // Enrich items with site_name / default_po_label for display
      const siteMap = Object.fromEntries((siteData || []).map(s => [s.id, s.name]));
      const poMap = Object.fromEntries((poData || []).map(p => [p.id, p]));
      const enrichedData = (data || []).map(item => {
        const po = item.default_po_id ? poMap[item.default_po_id] : null;
        const li = po && item.default_po_line_item ? (po.line_items || []).find(l => l.item_code === item.default_po_line_item) : null;
        return {
          ...item,
          site_name: item.site_id ? siteMap[item.site_id] || "Unknown" : (config.label === "Materials" ? "All sites" : "—"),
          default_po: po ? `${po.po_number}${li ? ` · ${li.item_code}` : ""}${po.status !== "active" ? " (inactive)" : ""}` : "—",
        };
      });
      setItems(enrichedData);
      setTenants(tenantData || []);
      setSites(siteData || []);
      setPOs(poData || []);
    } catch (err) {
      toast.error(`Failed to load ${config.label.toLowerCase()}`);
    }
  }, [config, hasPoField]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const openAdd = () => {
    setEditItem(null);
    const initial = {};
    config.fields.forEach(f => { initial[f.key] = ""; });
    setForm(initial);
    setSelectedTenants([]);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    const initial = {};
    config.fields.forEach(f => {
      if (f.type === "po_line_select") {
        initial[f.key] = item.default_po_id ? `${item.default_po_id}${item.default_po_line_item ? "::" + item.default_po_line_item : ""}` : "";
      } else {
        initial[f.key] = item[f.key] || "";
      }
    });
    setForm(initial);
    setSelectedTenants(item.tenant_ids || []);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const missing = config.fields.filter(f => f.required && !form[f.key]);
    if (missing.length) { toast.error(`${missing[0].label} is required`); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      config.fields.filter(f => f.type === "po_line_select").forEach(f => {
        const [poId, itemCode] = (form[f.key] || "").split("::");
        payload.default_po_id = poId || null;
        payload.default_po_line_item = itemCode || null;
      });
      if (config.supportsTenants) {
        payload.tenant_ids = selectedTenants;
      }
      if (editItem) {
        await api.put(`${config.endpoint}/${editItem.id}`, payload);
        toast.success("Updated");
      } else {
        await api.post(config.endpoint, payload);
        toast.success("Created");
      }
      setDialogOpen(false);
      await loadItems();
      if (editItem && hasPoField && payload.default_po_id) {
        const check = await api.post("/admin/materials/apply-default-pos", { material_id: editItem.id, dry_run: true }).catch(() => null);
        if (check?.cleared > 0 && window.confirm(`${check.cleared} existing movement(s) of this material are manually pinned to a different PO and would NOT bill to the new Default PO.\n\nRe-point them to the material's Default PO now?`)) {
          const res = await api.post("/admin/materials/apply-default-pos", { material_id: editItem.id });
          toast.success(`${res.cleared} movement(s) now bill to the material's Default PO`);
        }
      }
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`${config.endpoint}/${id}`);
      toast.success("Deleted");
      await loadItems();
    } catch (err) {
      toast.error("Failed to delete");
    }
  };

  const toggleTenant = (tenantId) => {
    setSelectedTenants(prev => 
      prev.includes(tenantId) 
        ? prev.filter(id => id !== tenantId)
        : [...prev, tenantId]
    );
  };

  const getTenantNames = (tenantIds) => {
    if (!tenantIds || tenantIds.length === 0) return "All Portals";
    return tenantIds.map(id => tenants.find(t => t.id === id)?.display_name || id).join(", ");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-muted-foreground text-sm"><span className="kpi-value">{items.length}</span> {config.label.toLowerCase()}</p>
        <Button onClick={openAdd} size="sm" data-testid={`add-${config.label.toLowerCase()}-button`} className="font-headline font-semibold">
          <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No {config.label.toLowerCase()} yet</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                {config.columns.map(col => (
                  <TableHead key={col} className="text-muted-foreground text-xs uppercase">{col.replace(/_/g, " ")}</TableHead>
                ))}
                {config.supportsTenants && tenants.length > 0 && (
                  <TableHead className="text-muted-foreground text-xs uppercase">Portals</TableHead>
                )}
                <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} className="border-border">
                  {config.columns.map(col => (
                    <TableCell key={col} className="text-sm">
                      {col === "hourly_rate" ? (item[col] ? `$${Number(item[col]).toFixed(2)}` : "—") 
                        : col === "payment_terms" ? (item[col] ? `Net ${item[col]}` : "—")
                        : (item[col] !== undefined && item[col] !== null && item[col] !== "" ? item[col] : "—")}
                    </TableCell>
                  ))}
                  {config.supportsTenants && tenants.length > 0 && (
                    <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate" title={getTenantNames(item.tenant_ids)}>
                      {getTenantNames(item.tenant_ids)}
                    </TableCell>
                  )}
                  <TableCell>
                    <Badge variant="outline" className={item.active !== false ? "bg-primary/20 text-primary border-primary/30 text-xs" : "bg-muted text-muted-foreground border-border text-xs"}>
                      {item.active !== false ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {config.label === "Sites" && (
                      <Button variant="ghost" size="icon" onClick={() => setGeoSite(item)} title="Geofence" data-testid={`geofence-button-${item.id}`}>
                        <Navigation className={`h-3.5 w-3.5 ${item.geofence ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => openEdit(item)}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    {globalAdmin && <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`bg-card border-border ${hasPoField ? "max-w-md" : "max-w-sm"}`}>
          <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} {config.label.slice(0, -1)}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {config.fields.map(f => (
              <div key={f.key} className="space-y-2">
                <Label className="text-sm text-muted-foreground">{f.label}{f.required ? " *" : ""}</Label>
                {f.type === "select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="bg-background border-border"><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "site_select" ? (
                  <Select value={form[f.key] || ""} onValueChange={(v) => setForm({ ...form, [f.key]: v })}>
                    <SelectTrigger className="bg-background border-border" data-testid={`lookup-${f.key}-select`}><SelectValue placeholder="Select site" /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.state})</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "po_line_select" ? (
                  <div className="space-y-1">
                    <Select value={form[f.key] || "__none__"} onValueChange={(v) => setForm({ ...form, [f.key]: v === "__none__" ? "" : v })}>
                      <SelectTrigger className="bg-background border-border" data-testid={`lookup-${f.key}-select`}><SelectValue placeholder="Auto-match (no default)" /></SelectTrigger>
                      <SelectContent className="bg-card border-border max-h-72">
                        <SelectItem value="__none__">Auto-match (no default)</SelectItem>
                        {pos.filter(p => p.status === "active").map(p => (
                          <React.Fragment key={p.id}>
                            <SelectItem value={p.id}><span className="font-mono">{p.po_number}</span>{p.description ? ` — ${p.description}` : ""}</SelectItem>
                            {(p.line_items || []).filter(li => li.item_code).map(li => (
                              <SelectItem key={`${p.id}::${li.item_code}`} value={`${p.id}::${li.item_code}`}>
                                <span className="pl-4 text-xs"><span className="font-mono">{p.po_number} · {li.item_code}</span>{li.description ? ` — ${li.description}` : ""}</span>
                              </SelectItem>
                            ))}
                          </React.Fragment>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Movements of this material bill to this PO. The invoice shows the PO / line-item description (SAP match) instead of the haul route.</p>
                  </div>
                ) : (
                  <Input
                    type={f.type === "number" ? "number" : "text"}
                    step={f.type === "number" ? "0.01" : undefined}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: f.type === "number" ? (e.target.value === "" ? "" : parseFloat(e.target.value)) : e.target.value })}
                    placeholder={f.placeholder || ""}
                    className="bg-background border-border"
                  />
                )}
              </div>
            ))}
            {/* Tenant Assignment */}
            {config.supportsTenants && tenants.length > 0 && (
              <div className="space-y-2 pt-2 border-t border-border">
                <Label className="text-sm text-muted-foreground">Assign to Portals</Label>
                <p className="text-xs text-muted-foreground mb-2">Leave empty to make available to all portals, or select specific portals.</p>
                <div className="flex flex-wrap gap-2">
                  {tenants.map(t => (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => toggleTenant(t.id)}
                      className={`px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        selectedTenants.includes(t.id)
                          ? "bg-primary/20 border-primary/50 text-primary"
                          : "bg-background border-border text-muted-foreground hover:border-primary/30"
                      }`}
                    >
                      {t.display_name || t.subdomain}
                    </button>
                  ))}
                </div>
                {selectedTenants.length === 0 && (
                  <p className="text-[10px] text-primary">Available to all portals (global)</p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {geoSite && (
        <GeofenceDialog
          site={geoSite}
          open={!!geoSite}
          onClose={() => setGeoSite(null)}
          onSaved={loadItems}
        />
      )}
    </div>
  );
}

function UsersTab({ globalAdmin }) {
  const [users, setUsers] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "contractor", contractor_id: "", tenant_id: "" });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [u, l, t] = await Promise.all([api.get("/admin/users"), api.get("/lookups"), api.get("/admin/tenants")]);
      setUsers(u || []);
      setContractors(l.contractors || []);
      setTenants(t || []);
    } catch (err) {
      toast.error("Failed to load users");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditUser(null);
    setForm({ email: "", name: "", password: "", role: "contractor", contractor_id: "", tenant_id: "" });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email || "", name: u.name || "", password: "", role: u.role || "contractor", contractor_id: u.contractor_id || "", tenant_id: u.tenant_id || "" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.name) { toast.error("Email and name are required"); return; }
    if (!editUser && !form.password) { toast.error("Password is required for new users"); return; }
    setSaving(true);
    try {
      if (editUser) {
        const payload = { name: form.name, email: form.email, role: form.role, contractor_id: form.contractor_id, tenant_id: form.tenant_id || null };
        if (form.password) payload.password = form.password;
        await api.put(`/admin/users/${editUser.id}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/auth/register", { ...form, tenant_id: form.tenant_id || null });
        toast.success("User created");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User deleted");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-muted-foreground text-sm"><span className="kpi-value">{users.length}</span> users</p>
        <Button onClick={openAdd} size="sm" data-testid="add-user-button" className="font-headline font-semibold">
          <UserPlus className="h-4 w-4 mr-1" strokeWidth={2} /> Add User
        </Button>
      </div>

      {users.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No users</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs uppercase">Name</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Email</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Role</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Portal</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-border" data-testid={`user-row-${u.id}`}>
                  <TableCell className="text-sm font-medium">{u.name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={
                      u.role === "admin" ? "bg-primary/20 text-primary border-primary/30 text-xs"
                        : u.role === "contractor" ? "bg-blue-500/20 text-blue-400 border-blue-500/30 text-xs"
                          : "bg-muted text-muted-foreground border-border text-xs"
                    }>{u.role}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{contractors.find(c => c.id === u.contractor_id)?.name || "—"}</TableCell>
                  <TableCell className="text-sm">
                    {u.tenant_id ? (
                      <Badge variant="outline" className="text-xs bg-purple-500/20 text-purple-400 border-purple-500/30">
                        {tenants.find(t => t.id === u.tenant_id)?.subdomain || "—"}
                      </Badge>
                    ) : <span className="text-muted-foreground text-xs">Default</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    {globalAdmin && <Button variant="ghost" size="icon" onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-headline">{editUser ? "Edit" : "Create"} User</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Name *</Label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Email *</Label>
              <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@company.com" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Password {editUser ? "(leave blank to keep)" : "*"}</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editUser ? "Leave blank to keep current" : "Password"} className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="admin">Global Admin</SelectItem>
                  <SelectItem value="tenant_admin">Tenant Admin</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.role === "contractor" && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Contractor Company</Label>
                <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v })}>
                  <SelectTrigger data-testid="user-contractor-select" className="bg-background border-border"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Tenant Portal</Label>
              <Select value={form.tenant_id || "none"} onValueChange={(v) => setForm({ ...form, tenant_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="user-tenant-select" className="bg-background border-border"><SelectValue placeholder="Default (all portals)" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="none">Default (all portals)</SelectItem>
                  {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.display_name} ({t.subdomain})</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Restricts this user to a specific client portal</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-submit" className="font-headline font-semibold">{saving ? "Saving..." : (editUser ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NotificationsTab() {
  const [recipients, setRecipients] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ email: "", name: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const data = await api.get("/admin/notification-recipients");
      setRecipients(data || []);
    } catch (err) {
      toast.error("Failed to load recipients");
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => { setEditItem(null); setForm({ email: "", name: "" }); setDialogOpen(true); };
  const openEdit = (item) => { setEditItem(item); setForm({ email: item.email, name: item.name || "" }); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/admin/notification-recipients/${editItem.id}`, form);
        toast.success("Recipient updated");
      } else {
        await api.post("/admin/notification-recipients", form);
        toast.success("Recipient added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/notification-recipients/${id}`);
      toast.success("Recipient removed");
      await loadData();
    } catch (err) { toast.error("Failed to delete"); }
  };

  const handleToggle = async (item) => {
    try {
      await api.put(`/admin/notification-recipients/${item.id}`, { active: !item.active });
      await loadData();
    } catch (err) { toast.error("Failed to update"); }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const res = await api.post("/admin/notification-recipients/test");
      toast.success(`Test email sent to ${res.recipients.join(", ")}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test");
    } finally { setTesting(false); }
  };

  return (
    <div data-testid="notifications-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-muted-foreground">Email notifications are sent when a report is submitted.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="border-border" onClick={handleTest} disabled={testing || recipients.filter(r => r.active).length === 0} data-testid="test-notification-button">
            <Send className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {testing ? "Sending..." : "Send Test"}
          </Button>
          <Button size="sm" onClick={openAdd} data-testid="add-recipient-button" className="font-headline font-semibold">
            <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> Add Recipient
          </Button>
        </div>
      </div>

      {recipients.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-12 text-center text-muted-foreground text-sm">No notification recipients configured</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs uppercase">Name</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Email</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.map((r) => (
                <TableRow key={r.id} className="border-border" data-testid={`recipient-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">{r.name || "-"}</TableCell>
                  <TableCell className="text-sm font-mono">{r.email}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={`cursor-pointer ${r.active ? "bg-primary/20 text-primary border-primary/30" : "bg-muted text-muted-foreground border-border"}`}
                      onClick={() => handleToggle(r)}
                      data-testid={`toggle-recipient-${r.id}`}
                    >
                      {r.active ? "Active" : "Paused"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(r)} data-testid={`edit-recipient-${r.id}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} data-testid={`delete-recipient-${r.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm" data-testid="recipient-dialog">
          <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Recipient</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Name</Label>
              <Input data-testid="recipient-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. BYK Operations" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Email *</Label>
              <Input data-testid="recipient-email-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@example.com" className="bg-background border-border" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-recipient-button" className="font-headline font-semibold">{saving ? "Saving..." : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TenantsTab() {
  const [tenants, setTenants] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ subdomain: "", display_name: "", logo_url: "", primary_color: "#6EE7B7", accent_color: "#34D399", welcome_message: "", backdate_policy: "daily", report_date_days_back: 90, monthly_catchup_day: 3, monthly_catchup_hour: 12, timezone: "America/Phoenix" });
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const t = await api.get("/admin/tenants");
      setTenants(t || []);
    } catch { toast.error("Failed to load tenants"); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const openAdd = () => {
    setEditItem(null);
    setForm({ subdomain: "", display_name: "", logo_url: "", primary_color: "#6EE7B7", accent_color: "#34D399", welcome_message: "", backdate_policy: "daily", report_date_days_back: 90, monthly_catchup_day: 3, monthly_catchup_hour: 12, timezone: "America/Phoenix" });
    setDialogOpen(true);
  };

  const openEdit = (t) => {
    setEditItem(t);
    // Derive backdate_policy from legacy fields if missing
    const derivedPolicy = t.backdate_policy || (t.report_date_unlocked ? "open" : "daily");
    setForm({
      subdomain: t.subdomain || "", display_name: t.display_name || "", logo_url: t.logo_url || "",
      primary_color: t.primary_color || "#6EE7B7", accent_color: t.accent_color || "#34D399",
      welcome_message: t.welcome_message || "",
      backdate_policy: derivedPolicy,
      report_date_days_back: t.report_date_days_back || 90,
      monthly_catchup_day: t.monthly_catchup_day || 3,
      monthly_catchup_hour: t.monthly_catchup_hour != null ? t.monthly_catchup_hour : 12,
      timezone: t.timezone || "America/Phoenix",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.subdomain || !form.display_name) { toast.error("Subdomain and display name are required"); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/admin/tenants/${editItem.id}`, form);
        toast.success("Tenant updated");
      } else {
        await api.post("/admin/tenants", form);
        toast.success("Tenant created");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save tenant");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/tenants/${id}`);
      toast.success("Tenant deleted");
      await loadData();
    } catch { toast.error("Failed to delete tenant"); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-muted-foreground text-sm"><span className="kpi-value">{tenants.length}</span> client portals</p>
        <Button onClick={openAdd} size="sm" data-testid="add-tenant-button" className="font-headline font-semibold">
          <Plus className="h-4 w-4 mr-1" strokeWidth={2} /> Add Tenant
        </Button>
      </div>

      {tenants.length === 0 ? (
        <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No tenants configured. Add a client portal to get started.</CardContent></Card>
      ) : (
        <Card className="border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground text-xs uppercase">Brand</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Portal URL</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Colors</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Report Date</TableHead>
                <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
                <TableHead className="text-right text-muted-foreground text-xs uppercase">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tenants.map((t) => (
                <TableRow key={t.id} className="border-border" data-testid={`tenant-row-${t.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {t.logo_url ? <img src={t.logo_url} alt="" className="h-6 w-6 object-contain rounded" /> : null}
                      <span className="text-sm font-medium">{t.display_name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm text-primary">ampmineops.online/{t.subdomain}</TableCell>
                  <TableCell>
                    <div className="flex gap-1.5">
                      <div className="w-5 h-5 rounded border border-border" style={{ backgroundColor: t.primary_color }} title={`Primary: ${t.primary_color}`} />
                      <div className="w-5 h-5 rounded border border-border" style={{ backgroundColor: t.accent_color }} title={`Accent: ${t.accent_color}`} />
                    </div>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const policy = t.backdate_policy || (t.report_date_unlocked ? "open" : "daily");
                      if (policy === "monthly_catchup") {
                        return (
                          <Badge variant="outline" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-xs" data-testid={`policy-badge-${t.id}`}>
                            <CalendarDays className="h-3 w-3 mr-1" strokeWidth={1.5} />Catch-up (D{t.monthly_catchup_day || 3} {(t.monthly_catchup_hour ?? 12).toString().padStart(2, "0")}:00)
                          </Badge>
                        );
                      }
                      if (policy === "open") {
                        return (
                          <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-xs" data-testid={`policy-badge-${t.id}`}>
                            <CalendarDays className="h-3 w-3 mr-1" strokeWidth={1.5} />Open ({t.report_date_days_back || 90}d)
                          </Badge>
                        );
                      }
                      return (
                        <Badge variant="outline" className="bg-muted text-muted-foreground border-border text-xs" data-testid={`policy-badge-${t.id}`}>
                          <Lock className="h-3 w-3 mr-1" strokeWidth={1.5} />Daily
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={t.active ? "bg-primary/20 text-primary border-primary/30 text-xs" : "bg-muted text-muted-foreground border-border text-xs"}>
                      {t.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`edit-tenant-${t.id}`}><Pencil className="h-3.5 w-3.5" strokeWidth={1.5} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(t.id)} data-testid={`delete-tenant-${t.id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" strokeWidth={1.5} /></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="tenant-dialog">
          <DialogHeader><DialogTitle className="font-headline">{editItem ? "Edit" : "Create"} Client Portal</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Portal ID *</Label>
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground whitespace-nowrap">ampmineops.online/</span>
                <Input data-testid="tenant-subdomain-input" value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })} placeholder="byk" className="bg-background border-border font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Display Name *</Label>
              <Input data-testid="tenant-name-input" value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} placeholder="BYK Mining" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Client Logo URL</Label>
              <Input data-testid="tenant-logo-input" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://example.com/logo.png" className="bg-background border-border text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border border-border bg-transparent" />
                  <Input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="bg-background border-border font-mono text-xs" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Accent Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border border-border bg-transparent" />
                  <Input value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })} className="bg-background border-border font-mono text-xs" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Welcome Message</Label>
              <Input data-testid="tenant-welcome-input" value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} placeholder="Welcome to BYK Mining Portal" className="bg-background border-border" />
            </div>
            <div className="space-y-3 p-3 rounded border border-border bg-background">
              <div>
                <Label className="text-sm font-medium">Report Date Policy</Label>
                <p className="text-xs text-muted-foreground mt-0.5">How far back can contractors enter daily reports?</p>
              </div>
              <div className="space-y-1.5">
                {[
                  { v: "daily", label: "Daily only", desc: "Today's date locked — contractors enter same-day reports" },
                  { v: "open", label: "Open backdating", desc: "Any date within a rolling window (e.g. 90 days)" },
                  { v: "monthly_catchup", label: "Monthly catch-up", desc: "Previous month is open until day X at hour Y, then locks to today" },
                ].map((opt) => (
                  <label key={opt.v} className={`flex items-start gap-2 p-2 rounded border cursor-pointer transition-colors ${form.backdate_policy === opt.v ? "border-emerald-500/40 bg-emerald-500/5" : "border-border hover:bg-accent/30"}`}>
                    <input
                      type="radio"
                      name="backdate_policy"
                      value={opt.v}
                      checked={form.backdate_policy === opt.v}
                      onChange={() => setForm({ ...form, backdate_policy: opt.v })}
                      data-testid={`backdate-policy-${opt.v}`}
                      className="mt-0.5 accent-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-[11px] text-muted-foreground">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              {form.backdate_policy === "open" && (
                <div className="flex items-center gap-2 pt-1">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Max days back:</Label>
                  <Input
                    type="number" min="1" max="365"
                    value={form.report_date_days_back}
                    onChange={(e) => setForm({ ...form, report_date_days_back: parseInt(e.target.value) || 90 })}
                    data-testid="report-date-days-back-input"
                    className="bg-background border-border w-20 text-sm"
                  />
                  <span className="text-xs text-muted-foreground">days</span>
                </div>
              )}

              {form.backdate_policy === "monthly_catchup" && (
                <div className="space-y-2 pt-1">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Cutoff day of month</Label>
                      <Input
                        type="number" min="1" max="28"
                        value={form.monthly_catchup_day}
                        onChange={(e) => setForm({ ...form, monthly_catchup_day: parseInt(e.target.value) || 3 })}
                        data-testid="catchup-day-input"
                        className="bg-background border-border text-sm mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Cutoff hour (0-23)</Label>
                      <Input
                        type="number" min="0" max="23"
                        value={form.monthly_catchup_hour}
                        onChange={(e) => setForm({ ...form, monthly_catchup_hour: parseInt(e.target.value) >= 0 ? parseInt(e.target.value) : 12 })}
                        data-testid="catchup-hour-input"
                        className="bg-background border-border text-sm mt-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Timezone</Label>
                    <Input
                      value={form.timezone}
                      onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                      data-testid="catchup-timezone-input"
                      placeholder="America/Phoenix"
                      className="bg-background border-border text-sm mt-1 font-mono"
                    />
                  </div>
                  <p className="text-[11px] text-emerald-400/80 leading-snug">
                    Example: Day <span className="font-mono">{form.monthly_catchup_day}</span> at <span className="font-mono">{(form.monthly_catchup_hour ?? 12).toString().padStart(2, "0")}:00</span> ({form.timezone}) → June reports must be submitted by Jul {form.monthly_catchup_day} {(form.monthly_catchup_hour ?? 12).toString().padStart(2, "0")}:00.
                  </p>
                </div>
              )}
            </div>
            {form.primary_color && (
              <div className="p-3 rounded border border-border bg-background">
                <p className="text-xs text-muted-foreground mb-2">Preview</p>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded" style={{ backgroundColor: form.primary_color }} />
                  <div className="w-8 h-8 rounded" style={{ backgroundColor: form.accent_color }} />
                  <span className="text-sm font-headline font-semibold" style={{ color: form.primary_color }}>{form.display_name || "Client Name"}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-tenant-submit" className="font-headline font-semibold">{saving ? "Saving..." : (editItem ? "Update" : "Create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PowerBITab() {
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    api.get("/admin/powerbi-key").then(res => { setApiKey(res.api_key || ""); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      const res = await api.put("/admin/powerbi-key");
      setApiKey(res.api_key);
      toast.success("API key regenerated");
    } catch (err) {
      toast.error("Failed to regenerate key");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const endpointUrl = `${backendUrl}/api/powerbi/reports?api_key=${apiKey}`;

  return (
    <div data-testid="powerbi-tab">
      <div className="space-y-6">
        <Card className="border-border bg-card">
          <CardContent className="py-6 space-y-4">
            <div>
              <h3 className="font-headline font-semibold text-sm mb-1">Power BI API Key</h3>
              <p className="text-xs text-muted-foreground mb-3">Use this key to authenticate Power BI Web connector requests.</p>
              {loading ? (
                <p className="text-sm text-muted-foreground">Loading...</p>
              ) : (
                <div className="flex items-center gap-2">
                  <Input value={apiKey} readOnly className="bg-background border-border font-mono text-xs" data-testid="powerbi-api-key-input" />
                  <Button variant="outline" size="icon" onClick={() => handleCopy(apiKey)} className="border-border shrink-0" data-testid="copy-api-key-button">
                    <Copy className="h-4 w-4" strokeWidth={1.5} />
                  </Button>
                  <Button variant="outline" size="icon" onClick={handleRegenerate} disabled={regenerating} className="border-border shrink-0" data-testid="regenerate-api-key-button">
                    <RefreshCw className={`h-4 w-4 ${regenerating ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  </Button>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-headline font-semibold text-sm mb-1">Endpoint URL</h3>
              <p className="text-xs text-muted-foreground mb-3">Use this URL in Power BI Desktop &gt; Get Data &gt; Web.</p>
              <div className="flex items-center gap-2">
                <Input value={endpointUrl} readOnly className="bg-background border-border font-mono text-xs" data-testid="powerbi-endpoint-input" />
                <Button variant="outline" size="icon" onClick={() => handleCopy(endpointUrl)} className="border-border shrink-0" data-testid="copy-endpoint-button">
                  <Copy className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            </div>

            <div className="border border-border rounded-md p-4 bg-background">
              <h3 className="font-headline font-semibold text-sm mb-2">Quick Setup Guide</h3>
              <ol className="text-xs text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Open <strong>Power BI Desktop</strong></li>
                <li>Go to <strong>Home &gt; Get Data &gt; Web</strong></li>
                <li>Paste the <strong>Endpoint URL</strong> above</li>
                <li>Power BI will load the JSON data — expand the <code className="bg-card px-1 py-0.5 rounded text-primary">data</code> column</li>
                <li>Apply transforms as needed, then click <strong>Close &amp; Apply</strong></li>
                <li>Set up <strong>Scheduled Refresh</strong> in Power BI Service for automatic updates</li>
              </ol>
            </div>

            <div className="border border-border rounded-md p-4 bg-background">
              <h3 className="font-headline font-semibold text-sm mb-2">Optional Filters</h3>
              <p className="text-xs text-muted-foreground mb-2">Append query parameters to filter the data:</p>
              <div className="font-mono text-xs text-muted-foreground space-y-1">
                <p><code className="text-primary">date_from</code>=2026-01-01</p>
                <p><code className="text-primary">date_to</code>=2026-01-31</p>
                <p><code className="text-primary">site_id</code>=&lt;site uuid&gt;</p>
                <p><code className="text-primary">contractor_id</code>=&lt;contractor uuid&gt;</p>
                <p><code className="text-primary">shift</code>=Day or Night</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InvoiceTab() {
  const [settings, setSettings] = useState({ enabled: false, day_of_month: 1 });
  const [reminderSettings, setReminderSettings] = useState({ 
    enabled: false, 
    trigger_type: "both", 
    escalation_days: [7, 14, 30], 
    default_due_days: 30 
  });
  const [invoices, setInvoices] = useState([]);
  const [balances, setBalances] = useState(null);
  const [reminderHistory, setReminderHistory] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [selectedContractorId, setSelectedContractorId] = useState("all");
  const [pos, setPos] = useState([]);
  const [selectedPoId, setSelectedPoId] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sending, setSending] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingReminderSettings, setSavingReminderSettings] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: "", payment_date: "", method: "", reference: "", notes: "" });
  const [savingPayment, setSavingPayment] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(null);
  const [showReminderHistory, setShowReminderHistory] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [s, rs, inv, bal, hist, contractorsList, poList] = await Promise.all([
        api.get("/admin/invoice-settings"),
        api.get("/admin/reminder-settings"),
        api.get("/admin/invoices"),
        api.get("/admin/invoices/balances"),
        api.get("/admin/reminder-history"),
        api.get("/admin/contractors"),
        api.get("/admin/purchase-orders").catch(() => []),
      ]);
      setSettings(s || { enabled: false, day_of_month: 1 });
      setReminderSettings(rs || { enabled: false, trigger_type: "both", escalation_days: [7, 14, 30], default_due_days: 30 });
      setInvoices(inv || []);
      setBalances(bal || null);
      setReminderHistory(hist || []);
      setContractors(contractorsList || []);
      setPos((poList || []).filter(p => p.status === "active"));
    } catch { toast.error("Failed to load invoice data"); }
  }, []);

  useEffect(() => {
    loadData();
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    setDateFrom(start.toISOString().slice(0, 10));
    setDateTo(end.toISOString().slice(0, 10));
  }, [loadData]);

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await api.put("/admin/invoice-settings", settings);
      setSettings(res);
      toast.success(settings.enabled ? `Auto-invoicing enabled: day ${settings.day_of_month} each month` : "Auto-invoicing disabled");
    } catch { toast.error("Failed to save settings"); }
    finally { setSavingSettings(false); }
  };

  const saveReminderSettings = async () => {
    setSavingReminderSettings(true);
    try {
      const res = await api.put("/admin/reminder-settings", reminderSettings);
      setReminderSettings(res);
      toast.success(reminderSettings.enabled ? "Payment reminders enabled" : "Payment reminders disabled");
    } catch { toast.error("Failed to save reminder settings"); }
    finally { setSavingReminderSettings(false); }
  };

  const sendInvoices = async () => {
    if (!dateFrom || !dateTo) { toast.error("Select a date range"); return; }
    setSending(true);
    try {
      const payload = { date_from: dateFrom, date_to: dateTo, mode: replaceExisting ? "replace" : "skip" };
      if (selectedContractorId && selectedContractorId !== "all") {
        payload.contractor_id = selectedContractorId;
      }
      if (selectedPoId && selectedPoId !== "all") {
        payload.po_id = selectedPoId;
      }
      const res = await api.post("/admin/invoices/generate", payload);
      const parts = [];
      if (res.created) parts.push(`${res.created} created`);
      if (res.replaced) parts.push(`${res.replaced} replaced`);
      if (res.skipped_existing) parts.push(`${res.skipped_existing} skipped (already exist)`);
      if (res.skipped_has_payments) parts.push(`${res.skipped_has_payments} kept (has payments)`);
      if (res.skipped_no_data) parts.push(`${res.skipped_no_data} preserved (no new data)`);
      const msg = parts.length ? parts.join(", ") : "Nothing to do for this period";
      if ((res.created + res.replaced) > 0) {
        toast.success(msg);
      } else {
        toast.message(msg);
      }
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to generate invoices"); }
    finally { setSending(false); }
  };

  const downloadInvoicePdf = async (invoiceId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (err) { toast.error("Failed to download PDF"); }
  };

  const downloadInvoiceXlsx = async (invoiceId, variant = "xlsx") => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${invoiceId}/${variant}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!response.ok) throw new Error("Failed to download");
      const blob = await response.blob();
      // Try to extract filename from Content-Disposition; fallback to invoice-{id}.xlsx
      const cd = response.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="?([^"]+)"?/i);
      const fname = match ? match[1] : `invoice-${invoiceId}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error("Failed to download Excel"); }
  };

  const sendSingleInvoice = async (invoiceId) => {
    try {
      const res = await api.post(`/admin/invoices/${invoiceId}/send`);
      if (res.ok) {
        toast.success(`Invoice sent to ${res.recipients?.length || 0} recipient(s)`);
      } else {
        toast.error("Failed to send invoice");
      }
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send invoice"); }
  };

  const sendManualReminder = async (invoiceId) => {
    setSendingReminder(invoiceId);
    try {
      await api.post(`/admin/invoices/${invoiceId}/send-reminder`);
      toast.success("Payment reminder sent");
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send reminder"); }
    finally { setSendingReminder(null); }
  };

  const openPayment = (inv) => {
    setPaymentDialog(inv);
    setPaymentForm({ amount: "", payment_date: new Date().toISOString().slice(0, 10), method: "Wire Transfer", reference: "", notes: "" });
  };

  const recordPayment = async () => {
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    setSavingPayment(true);
    try {
      await api.post(`/admin/invoices/${paymentDialog.id}/payments`, { ...paymentForm, amount: parseFloat(paymentForm.amount) });
      toast.success("Payment recorded");
      setPaymentDialog(null);
      await loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to record payment"); }
    finally { setSavingPayment(false); }
  };

  const deletePayment = async (invoiceId, paymentId) => {
    try {
      await api.delete(`/admin/invoices/${invoiceId}/payments/${paymentId}`);
      toast.success("Payment deleted");
      await loadData();
    } catch { toast.error("Failed to delete payment"); }
  };

  const deleteInvoice = async (invoiceId, contractorName) => {
    if (!window.confirm(`Delete invoice for ${contractorName}? This will also delete the PDF file. This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/invoices/${invoiceId}`);
      toast.success("Invoice deleted");
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete invoice");
    }
  };

  const statusBadge = (ps) => {
    const map = {
      paid: "bg-primary/20 text-primary border-primary/30",
      partially_paid: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
      unpaid: "bg-red-500/20 text-red-400 border-red-500/30",
      overdue: "bg-orange-500/20 text-orange-400 border-orange-500/30",
    };
    return map[ps] || "bg-muted text-muted-foreground border-border";
  };

  const urgencyBadge = (u) => {
    const map = {
      urgent: "bg-red-500/20 text-red-400 border-red-500/30",
      warning: "bg-orange-500/20 text-orange-400 border-orange-500/30",
      reminder: "bg-primary/20 text-primary border-primary/30",
    };
    return map[u] || "bg-muted text-muted-foreground border-border";
  };

  const bs = balances?.summary || {};

  return (
    <div className="space-y-6">
      {/* Outstanding Balances Dashboard */}
      {balances && balances.contractors?.length > 0 && (
        <div data-testid="balances-dashboard">
          <h3 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Outstanding Balances</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <Card className="border-border bg-card"><CardContent className="py-3 px-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Invoiced</p>
              <p className="kpi-value text-lg">${bs.total_invoiced?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 px-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total Paid</p>
              <p className="kpi-value text-lg text-primary">${bs.total_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </CardContent></Card>
            <Card className="border-border bg-card"><CardContent className="py-3 px-4">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Outstanding</p>
              <p className="kpi-value text-lg text-orange-400">${bs.total_outstanding?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </CardContent></Card>
          </div>
          <Card className="border-border bg-card overflow-hidden mb-4">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">Invoiced</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">Paid</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">Outstanding</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase text-right">Invoices</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {balances.contractors.map((c) => (
                  <TableRow key={c.contractor_id} className="border-border">
                    <TableCell className="text-sm font-medium">{c.contractor_name}</TableCell>
                    <TableCell className="text-sm text-right font-mono">${c.total_invoiced?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-sm text-right font-mono text-primary">${c.total_paid?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-sm text-right font-mono font-semibold text-orange-400">${c.outstanding?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="text-sm text-right">{c.invoice_count} ({c.unpaid_count} unpaid)</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      )}

      {/* Auto Schedule Settings */}
      <Card className="border-border bg-card">
        <CardContent className="py-4 px-5">
          <h3 className="font-headline text-sm font-semibold mb-4 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-primary" strokeWidth={1.5} /> Auto-Invoice Schedule
          </h3>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex items-center gap-3">
              <Label className="text-sm text-muted-foreground">Enable</Label>
              <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="invoice-auto-toggle" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Day of Month</Label>
              <Select value={String(settings.day_of_month)} onValueChange={(v) => setSettings({ ...settings, day_of_month: parseInt(v) })}>
                <SelectTrigger className="w-[100px] bg-background border-border text-sm" data-testid="invoice-day-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-[200px]">
                  {Array.from({ length: 28 }, (_, i) => <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={saveSettings} disabled={savingSettings} variant="outline" size="sm" className="border-border" data-testid="save-invoice-settings">
              {savingSettings ? "Saving..." : "Save Schedule"}
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">
            {settings.enabled ? `Invoices auto-sent on day ${settings.day_of_month} of each month at 06:00 UTC.` : "Auto-invoicing is disabled. Use manual trigger below."}
          </p>
        </CardContent>
      </Card>

      {/* Payment Reminders */}
      <Card className="border-border bg-card">
        <CardContent className="py-4 px-5">
          <h3 className="font-headline text-sm font-semibold mb-4 flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" strokeWidth={1.5} /> Payment Reminders
          </h3>
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground">Enable Reminders</Label>
                <Switch 
                  checked={reminderSettings.enabled} 
                  onCheckedChange={(v) => setReminderSettings({ ...reminderSettings, enabled: v })} 
                  data-testid="reminder-enable-toggle" 
                />
              </div>
              <Button onClick={saveReminderSettings} disabled={savingReminderSettings} variant="outline" size="sm" className="border-border" data-testid="save-reminder-settings">
                {savingReminderSettings ? "Saving..." : "Save Reminder Settings"}
              </Button>
            </div>
            
            {reminderSettings.enabled && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-border">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Trigger Based On</Label>
                  <Select value={reminderSettings.trigger_type} onValueChange={(v) => setReminderSettings({ ...reminderSettings, trigger_type: v })}>
                    <SelectTrigger className="bg-background border-border text-sm" data-testid="reminder-trigger-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="invoice_date">Days after Invoice</SelectItem>
                      <SelectItem value="due_date">Days after Due Date</SelectItem>
                      <SelectItem value="both">Both (Whichever is later)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Default Due Days</Label>
                  <Select value={String(reminderSettings.default_due_days)} onValueChange={(v) => setReminderSettings({ ...reminderSettings, default_due_days: parseInt(v) })}>
                    <SelectTrigger className="bg-background border-border text-sm" data-testid="reminder-due-days-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      <SelectItem value="15">15 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="45">45 days</SelectItem>
                      <SelectItem value="60">60 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Escalation Schedule</Label>
                  <p className="text-sm text-muted-foreground bg-background rounded px-2 py-1.5 border border-border">
                    Day {reminderSettings.escalation_days?.[0] || 7}, {reminderSettings.escalation_days?.[1] || 14}, {reminderSettings.escalation_days?.[2] || 30}
                  </p>
                </div>
              </div>
            )}
            
            <p className="text-[10px] text-muted-foreground">
              {reminderSettings.enabled 
                ? `Escalating reminders sent at Day 7 (friendly), Day 14 (warning), Day 30 (urgent). Runs daily at 08:00 UTC.`
                : "Payment reminders are disabled. Enable to automatically notify contractors of outstanding balances."}
            </p>
            
            {reminderHistory.length > 0 && (
              <div className="pt-2 border-t border-border">
                <button 
                  onClick={() => setShowReminderHistory(!showReminderHistory)}
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                  data-testid="toggle-reminder-history"
                >
                  {showReminderHistory ? "Hide" : "Show"} Reminder History ({reminderHistory.length})
                </button>
                {showReminderHistory && (
                  <div className="mt-3 max-h-[200px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border hover:bg-transparent">
                          <TableHead className="text-muted-foreground text-[10px] uppercase py-2">Sent</TableHead>
                          <TableHead className="text-muted-foreground text-[10px] uppercase py-2">Contractor</TableHead>
                          <TableHead className="text-muted-foreground text-[10px] uppercase py-2">Balance</TableHead>
                          <TableHead className="text-muted-foreground text-[10px] uppercase py-2">Level</TableHead>
                          <TableHead className="text-muted-foreground text-[10px] uppercase py-2">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reminderHistory.slice(0, 10).map((r) => (
                          <TableRow key={r.id} className="border-border">
                            <TableCell className="text-xs py-1.5 font-mono">{r.sent_at?.slice(0, 10)}</TableCell>
                            <TableCell className="text-xs py-1.5">{r.contractor_name}</TableCell>
                            <TableCell className="text-xs py-1.5 font-mono">${(r.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                            <TableCell className="py-1.5">
                              <Badge variant="outline" className={`text-[10px] ${urgencyBadge(r.urgency)}`}>
                                {r.urgency}
                              </Badge>
                            </TableCell>
                            <TableCell className="py-1.5">
                              <Badge variant="outline" className={`text-[10px] ${r.status === 'sent' ? 'bg-primary/20 text-primary border-primary/30' : 'bg-red-500/20 text-red-400 border-red-500/30'}`}>
                                {r.status}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Manual Trigger */}
      <Card className="border-border bg-card">
        <CardContent className="py-4 px-5">
          <h3 className="font-headline text-sm font-semibold mb-4 flex items-center gap-2">
            <Send className="h-4 w-4 text-primary" strokeWidth={1.5} /> Generate Invoices
          </h3>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[150px] bg-background border-border text-sm" data-testid="invoice-date-from" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[150px] bg-background border-border text-sm" data-testid="invoice-date-to" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Contractor</Label>
              <Select value={selectedContractorId} onValueChange={setSelectedContractorId}>
                <SelectTrigger className="w-[220px] bg-background border-border text-sm" data-testid="invoice-contractor-select">
                  <SelectValue placeholder="All contractors" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All contractors</SelectItem>
                  {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">Project / PO</Label>
              <Select value={selectedPoId} onValueChange={setSelectedPoId}>
                <SelectTrigger className="w-[260px] bg-background border-border text-sm" data-testid="invoice-po-select">
                  <SelectValue placeholder="Consolidated (all POs)" />
                </SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">
                  <SelectItem value="all">Consolidated (all POs)</SelectItem>
                  <SelectItem value="__all__">Every PO — one invoice per PO</SelectItem>
                  {pos.filter(p => selectedContractorId === "all" || !p.contractor_id || p.contractor_id === selectedContractorId).map(p => (
                    <SelectItem key={p.id} value={p.id}><span className="font-mono">{p.po_number}</span>{p.description ? ` — ${p.description.slice(0, 40)}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={sendInvoices} disabled={sending} data-testid="send-invoices-btn" className="font-headline font-semibold">
              {sending ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileText className="h-4 w-4 mr-1.5" strokeWidth={1.5} />}
              {sending ? "Generating..." : "Generate Invoices"}
            </Button>
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none pb-2" title="If an invoice already exists for the same contractor + period, replace it. Invoices that already have payments recorded will NEVER be replaced automatically.">
              <input
                type="checkbox"
                checked={replaceExisting}
                onChange={(e) => setReplaceExisting(e.target.checked)}
                data-testid="replace-existing-toggle"
                className="h-3.5 w-3.5 accent-emerald-500"
              />
              Replace existing for this period
            </label>
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">Pick a <strong>Project / PO</strong> to invoice only the lines billed to that PO — you can generate one invoice per PO for the same contractor and period. Idempotent. Re-running on the same period <strong>skips</strong> existing invoices by default. Tick "Replace" to regenerate unpaid invoices (paid ones are always preserved).</p>
        </CardContent>
      </Card>

      {/* Invoice History with Payment Tracking */}
      <div>
        <h3 className="font-headline text-sm font-semibold tracking-wide uppercase text-muted-foreground mb-3">Invoice History & Payments</h3>
        {invoices.length === 0 ? (
          <Card className="border-border bg-card"><CardContent className="py-8 text-center text-muted-foreground text-sm">No invoices generated yet.</CardContent></Card>
        ) : (
          <Card className="border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
            <Table className="min-w-[1040px]">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground text-xs uppercase py-3">Contractor / Project</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3">Billing Period</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3">Due</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3 text-right">Invoiced</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3 text-right">Paid</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3 text-right">Balance</TableHead>
                  <TableHead className="text-muted-foreground text-xs uppercase py-3">Status</TableHead>
                  <TableHead className="text-right text-muted-foreground text-xs uppercase py-3">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} className="border-border hover:bg-white/[0.02]" data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="py-3 align-middle">
                      <div className="font-medium text-sm leading-tight">{inv.contractor_name}</div>
                      <div className="mt-1" data-testid={`invoice-po-${inv.id}`}>
                        {inv.po_number ? (
                          <Badge variant="outline" className="font-mono text-[10px] whitespace-nowrap bg-blue-500/10 text-blue-400 border-blue-500/30" title={inv.po_description || ""}>{inv.po_number}</Badge>
                        ) : <span className="text-[11px] text-muted-foreground">Consolidated · all POs</span>}
                      </div>
                    </TableCell>
                    <TableCell className="py-3 align-middle whitespace-nowrap">
                      <div className="text-sm font-mono leading-tight">{inv.period_from} <span className="text-muted-foreground">→</span> {inv.period_to}</div>
                      <div className="text-[11px] text-muted-foreground mt-1">Generated {inv.created_at?.slice(0, 10)}</div>
                    </TableCell>
                    <TableCell className="py-3 align-middle text-sm font-mono text-muted-foreground whitespace-nowrap">{inv.due_date || "—"}</TableCell>
                    <TableCell className="py-3 align-middle text-sm text-right font-mono whitespace-nowrap">${(inv.total_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="py-3 align-middle text-sm text-right font-mono text-primary whitespace-nowrap">${(inv.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="py-3 align-middle text-sm text-right font-mono font-semibold text-orange-400 whitespace-nowrap">${(inv.balance ?? inv.total_cost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell className="py-3 align-middle">
                      <Badge variant="outline" className={`text-xs whitespace-nowrap ${statusBadge(inv.payment_status || "unpaid")}`}>
                        {(inv.payment_status || "unpaid").replace("_", " ")}
                      </Badge>
                      <div className="text-[10px] text-muted-foreground mt-1 capitalize">{inv.status === "sent" ? "emailed" : inv.status}</div>
                    </TableCell>
                    <TableCell className="py-3 align-middle text-right">
                      <div className="inline-flex items-center gap-0.5 whitespace-nowrap">
                        <Button variant="ghost" size="icon" onClick={() => downloadInvoicePdf(inv.id)} data-testid={`download-invoice-${inv.id}`} className="h-8 w-8" title="Download PDF">
                          <FileText className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => downloadInvoiceXlsx(inv.id)} data-testid={`download-invoice-xlsx-${inv.id}`} className="h-8 w-8 text-emerald-400" title="Download Excel">
                          <FileSpreadsheet className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => downloadInvoiceXlsx(inv.id, "sap-xlsx")} data-testid={`sap-export-${inv.id}`} className="h-8 px-1.5 text-[10px] font-mono text-blue-400" title="SAP Upload Sheet (one row per PO line item)">
                          SAP
                        </Button>
                        <span className="w-px h-5 bg-border mx-1" />
                        {inv.status === "generated" && (
                          <Button variant="ghost" size="icon" onClick={() => sendSingleInvoice(inv.id)} data-testid={`send-invoice-${inv.id}`} className="h-8 w-8 text-primary" title="Send Email">
                            <Send className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                        {inv.status === "sent" && (
                          <Button variant="ghost" size="icon" onClick={() => sendSingleInvoice(inv.id)} data-testid={`resend-invoice-${inv.id}`} className="h-8 w-8 text-primary" title="Resend Email">
                            <RefreshCw className="h-4 w-4" strokeWidth={1.5} />
                          </Button>
                        )}
                        {inv.status === "sent" && inv.payment_status !== "paid" && (
                          <>
                            <Button variant="ghost" size="icon" onClick={() => sendManualReminder(inv.id)} disabled={sendingReminder === inv.id} data-testid={`send-reminder-${inv.id}`} className="h-8 w-8 text-orange-400" title="Send Payment Reminder">
                              {sendingReminder === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" strokeWidth={1.5} />}
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => openPayment(inv)} data-testid={`record-payment-${inv.id}`} className="h-8 w-8 text-primary" title="Record Payment">
                              <DollarSign className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          </>
                        )}
                        <span className="w-px h-5 bg-border mx-1" />
                        <Button variant="ghost" size="icon" onClick={() => deleteInvoice(inv.id, inv.contractor_name)} data-testid={`delete-invoice-${inv.id}`} className="h-8 w-8 text-destructive" title="Delete Invoice">
                          <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </Card>
        )}
        <p className="text-[11px] text-muted-foreground mt-2 flex flex-wrap gap-x-4 gap-y-1">
          <span><FileText className="inline h-3 w-3 mr-1" />PDF</span>
          <span><FileSpreadsheet className="inline h-3 w-3 mr-1 text-emerald-400" />Excel</span>
          <span><span className="font-mono text-blue-400 mr-1">SAP</span>Upload sheet</span>
          <span><Send className="inline h-3 w-3 mr-1 text-primary" />Send</span>
          <span><RefreshCw className="inline h-3 w-3 mr-1 text-primary" />Resend</span>
          <span><Bell className="inline h-3 w-3 mr-1 text-orange-400" />Reminder</span>
          <span><DollarSign className="inline h-3 w-3 mr-1 text-primary" />Record payment</span>
          <span><Trash2 className="inline h-3 w-3 mr-1 text-destructive" />Delete</span>
        </p>
      </div>

      {/* Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(v) => !v && setPaymentDialog(null)}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="payment-dialog">
          <DialogHeader>
            <DialogTitle className="font-headline">Record Payment</DialogTitle>
            {paymentDialog && (
              <p className="text-sm text-muted-foreground">
                {paymentDialog.contractor_name} — Balance: <span className="text-orange-400 font-mono font-semibold">${(paymentDialog.balance ?? paymentDialog.total_cost ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Amount ($) *</Label>
              <Input data-testid="payment-amount" type="number" step="0.01" value={paymentForm.amount} onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })} placeholder="0.00" className="bg-background border-border font-mono" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Payment Date</Label>
              <Input data-testid="payment-date" type="date" value={paymentForm.payment_date} onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Method</Label>
              <Select value={paymentForm.method} onValueChange={(v) => setPaymentForm({ ...paymentForm, method: v })}>
                <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Reference / Transaction #</Label>
              <Input data-testid="payment-reference" value={paymentForm.reference} onChange={(e) => setPaymentForm({ ...paymentForm, reference: e.target.value })} placeholder="TXN-12345" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Notes</Label>
              <Input data-testid="payment-notes" value={paymentForm.notes} onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })} placeholder="Optional notes..." className="bg-background border-border" />
            </div>
            {/* Payment History for this invoice */}
            {paymentDialog?.payments?.length > 0 && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Previous Payments</p>
                {paymentDialog.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-xs py-1.5 border-b border-border/50 last:border-0">
                    <div>
                      <span className="font-mono text-primary">${(Number(p.amount) || 0).toFixed(2)}</span>
                      <span className="text-muted-foreground ml-2">{p.payment_date}</span>
                      {p.method && <span className="text-muted-foreground ml-1">({p.method})</span>}
                    </div>
                    <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => deletePayment(paymentDialog.id, p.id)}>
                      <Trash2 className="h-3 w-3 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialog(null)} className="border-border">Cancel</Button>
            <Button onClick={recordPayment} disabled={savingPayment} data-testid="submit-payment" className="font-headline font-semibold">
              {savingPayment ? "Recording..." : "Record Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== PURCHASE ORDERS TAB ====================
function PurchaseOrdersTab() {
  const [pos, setPOs] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [activities, setActivities] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    po_number: "", description: "", tenant_id: "", contractor_id: "", site_id: "",
    start_date: "", end_date: "", total_amount: "", notes: "",
    coverage: { types: [], equipment_ids: [], material_ids: [], activity_types: [], rate_descriptions: [] },
    line_items: [],
  });

  const COVERAGE_TYPES = ["Equipment", "Hauling", "Stripping", "Labor", "Supervision"];
  const ACTIVITY_TYPES = activities.length ? activities.map(a => a.name) : ["Loading", "Stripping", "Reclamation", "Hauling", "Road Maintenance", "Watering", "Dust Control", "Push/Stockpile", "Stockpile Mgmt"];

  const loadData = async () => {
    try {
      const [poData, lookups, tenantData] = await Promise.all([
        api.get("/admin/purchase-orders/utilization"),
        api.get("/lookups"),
        api.get("/admin/tenants").catch(() => []),
      ]);
      setPOs(poData || []);
      setContractors(lookups.contractors || []);
      setSites(lookups.sites || []);
      setEquipment(lookups.equipment || []);
      setMaterials(lookups.materials || []);
      setActivities(lookups.activities || []);
      setTenants(tenantData || []);
    } catch (err) {
      toast.error("Failed to load PO data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getContractorName = (id) => contractors.find(c => c.id === id)?.name || "—";
  const getSiteName = (id) => sites.find(s => s.id === id)?.name || "—";

  const openAdd = () => {
    setEditItem(null);
    setForm({
      po_number: "", description: "", tenant_id: tenants[0]?.id || "", contractor_id: "", site_id: "",
      start_date: "", end_date: "", total_amount: "", notes: "",
      coverage: { types: [], equipment_ids: [], material_ids: [], activity_types: [], rate_descriptions: [] },
      line_items: [],
    });
    setDialogOpen(true);
  };

  const openEdit = (po) => {
    setEditItem(po);
    setForm({
      po_number: po.po_number || "",
      description: po.description || "",
      tenant_id: po.tenant_id || "",
      contractor_id: po.contractor_id || "",
      site_id: po.site_id || "",
      start_date: po.start_date || "",
      end_date: po.end_date || "",
      total_amount: String(po.total_amount || ""),
      notes: po.notes || "",
      coverage: po.coverage || { types: [], equipment_ids: [], material_ids: [], activity_types: [], rate_descriptions: [] },
      line_items: po.line_items || [],
    });
    setDialogOpen(true);
  };

  const clonePO = async (po) => {
    const nextYear = new Date().getFullYear() + 1;
    const newPO = {
      po_number: po.po_number.replace(/\d{4}/, String(nextYear)),
      description: po.description,
      tenant_id: po.tenant_id,
      contractor_id: po.contractor_id,
      site_id: po.site_id,
      start_date: po.start_date?.replace(/\d{4}/, String(nextYear)) || "",
      end_date: po.end_date?.replace(/\d{4}/, String(nextYear)) || "",
      total_amount: po.total_amount,
      notes: `Cloned from ${po.po_number}`,
      coverage: po.coverage,
    };
    try {
      await api.post("/admin/purchase-orders", newPO);
      toast.success(`Cloned to ${newPO.po_number}`);
      await loadData();
    } catch { toast.error("Failed to clone PO"); }
  };

  const handleSave = async () => {
    if (!form.po_number || !form.total_amount) {
      toast.error("PO Number and Total Amount are required");
      return;
    }
    try {
      const payload = { ...form, total_amount: parseFloat(form.total_amount) };
      if (editItem) {
        await api.put(`/admin/purchase-orders/${editItem.id}`, payload);
        toast.success("PO updated");
      } else {
        await api.post("/admin/purchase-orders", payload);
        toast.success("PO created");
      }
      setDialogOpen(false);
      await loadData();
    } catch { toast.error("Failed to save PO"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this Purchase Order?")) return;
    try {
      await api.delete(`/admin/purchase-orders/${id}`);
      toast.success("PO deleted");
      await loadData();
    } catch { toast.error("Failed to delete PO"); }
  };

  const toggleCoverage = (field, value) => {
    setForm(prev => {
      const arr = prev.coverage[field] || [];
      const updated = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, coverage: { ...prev.coverage, [field]: updated } };
    });
  };

  const utilizationBar = (pct, warning) => {
    const color = warning === "over" ? "bg-red-500" : warning === "high" ? "bg-amber-500" : "bg-emerald-500";
    return (
      <div className="flex items-center gap-2 w-36">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
          <div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        <span className={`text-xs font-mono ${warning === "over" ? "text-red-400" : warning === "high" ? "text-amber-400" : "text-muted-foreground"}`}>{pct}%</span>
      </div>
    );
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading POs...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{pos.length} purchase order(s)</span>
        <Button size="sm" onClick={openAdd} data-testid="add-po-button">
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> New PO
        </Button>
      </div>

      <Card className="border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-xs uppercase">PO #</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Description</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Period</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Budget</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Used</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Utilization</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pos.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No purchase orders. Click "New PO" to create one.</TableCell></TableRow>
            ) : (
              pos.map((po) => (
                <TableRow key={po.id} className="border-border">
                  <TableCell className="font-mono text-sm font-medium">{po.po_number}</TableCell>
                  <TableCell className="text-sm max-w-[200px] truncate">{po.description || "—"}</TableCell>
                  <TableCell className="text-sm">{getContractorName(po.contractor_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{po.start_date} — {po.end_date}</TableCell>
                  <TableCell className="text-right font-mono text-sm">${parseFloat(po.total_amount || 0).toLocaleString()}</TableCell>
                  <TableCell className="text-right font-mono text-sm">${parseFloat(po.used_amount || 0).toLocaleString()}</TableCell>
                  <TableCell>{utilizationBar(po.utilization_pct || 0, po.warning)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Clone to next year" onClick={() => clonePO(po)} data-testid={`clone-po-${po.id}`}>
                        <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(po)} data-testid={`edit-po-${po.id}`}>
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(po.id)} data-testid={`delete-po-${po.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-headline">{editItem ? "Edit" : "Create"} Purchase Order</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Define PO coverage and budget</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">PO Number *</Label>
                <Input data-testid="po-number-input" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} placeholder="PO-2026-001" className="bg-background border-border font-mono" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Total Amount ($) *</Label>
                <Input data-testid="po-amount-input" type="number" step="0.01" min="0" value={form.total_amount} onChange={(e) => setForm({ ...form, total_amount: e.target.value })} className="bg-background border-border font-mono" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Description</Label>
              <Input data-testid="po-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Annual equipment and hauling contract" className="bg-background border-border" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Contractor</Label>
                <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="po-contractor-select"><SelectValue placeholder="All contractors" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none">All contractors</SelectItem>
                    {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Site</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="po-site-select"><SelectValue placeholder="All sites" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="none">All sites</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Start Date</Label>
                <Input type="date" data-testid="po-start-date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="bg-background border-border" />
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">End Date</Label>
                <Input type="date" data-testid="po-end-date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="bg-background border-border" />
              </div>
            </div>
            {/* Coverage Rules */}
            <div className="space-y-3 p-3 rounded border border-border bg-background">
              <Label className="text-sm font-medium">Coverage Rules</Label>
              <p className="text-xs text-muted-foreground">Leave empty to cover all. Select specific items to restrict.</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Cost Types</Label>
                <div className="flex flex-wrap gap-1.5">
                  {COVERAGE_TYPES.map(t => (
                    <button key={t} type="button" onClick={() => toggleCoverage("types", t)}
                      className={`px-2 py-1 rounded text-xs border cursor-pointer transition-colors ${form.coverage.types.includes(t) ? 'bg-primary/20 text-primary border-primary/30' : 'bg-muted text-muted-foreground border-border'}`}
                    >{t}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Activity Types</Label>
                <div className="flex flex-wrap gap-1.5">
                  {ACTIVITY_TYPES.map(a => (
                    <button key={a} type="button" onClick={() => toggleCoverage("activity_types", a)}
                      className={`px-2 py-1 rounded text-xs border cursor-pointer transition-colors ${form.coverage.activity_types.includes(a) ? 'bg-primary/20 text-primary border-primary/30' : 'bg-muted text-muted-foreground border-border'}`}
                    >{a}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Specific Equipment (optional)</Label>
                <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
                  {equipment.map(e => (
                    <button key={e.id} type="button" onClick={() => toggleCoverage("equipment_ids", e.id)}
                      className={`px-2 py-0.5 rounded text-xs border cursor-pointer transition-colors ${form.coverage.equipment_ids.includes(e.id) ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-muted text-muted-foreground border-border'}`}
                    >{e.unit_id}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Specific Materials (optional)</Label>
                <div className="flex flex-wrap gap-1.5">
                  {materials.map(m => (
                    <button key={m.id} type="button" onClick={() => toggleCoverage("material_ids", m.id)}
                      className={`px-2 py-0.5 rounded text-xs border cursor-pointer transition-colors ${form.coverage.material_ids.includes(m.id) ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-muted text-muted-foreground border-border'}`}
                    >{m.name}</button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Route/Rate Keywords (for Hauling PO matching)</Label>
                <Input
                  placeholder="e.g., Helms, Miller, Dubose (comma-separated)"
                  value={(form.coverage.rate_descriptions || []).join(", ")}
                  onChange={(e) => {
                    const vals = e.target.value.split(",").map(v => v.trim()).filter(Boolean);
                    setForm(prev => ({ ...prev, coverage: { ...prev.coverage, rate_descriptions: vals } }));
                  }}
                  data-testid="po-rate-descriptions"
                  className="bg-background border-border text-sm"
                />
                <p className="text-[10px] text-muted-foreground">Matches material movements whose rate card description contains these keywords</p>
              </div>
            </div>
            {/* Line Items */}
            <div className="space-y-3 p-3 rounded border border-border bg-background">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">PO Line Items (optional)</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => setForm(prev => ({ ...prev, line_items: [...prev.line_items, { item_code: "", description: "", activity_types: [], rate_descriptions: [] }] }))}>
                  <Plus className="h-3 w-3 mr-1" />Add Item
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Define item codes (ITM10, ITM50...) to sub-categorize costs within this PO by activity.</p>
              {form.line_items.map((li, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 rounded border border-border/50 bg-card">
                  <div className="flex-1 space-y-2">
                    <div className="flex gap-2">
                      <Input placeholder="ITM10" value={li.item_code} onChange={(e) => { const items = [...form.line_items]; items[idx].item_code = e.target.value; setForm({ ...form, line_items: items }); }} className="bg-background border-border w-20 text-xs font-mono" />
                      <Input placeholder="Activity description" value={li.description} onChange={(e) => { const items = [...form.line_items]; items[idx].description = e.target.value; setForm({ ...form, line_items: items }); }} className="bg-background border-border flex-1 text-xs" />
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {ACTIVITY_TYPES.map(a => (
                        <button key={a} type="button" onClick={() => {
                          const items = [...form.line_items];
                          const acts = items[idx].activity_types || [];
                          items[idx].activity_types = acts.includes(a) ? acts.filter(x => x !== a) : [...acts, a];
                          setForm({ ...form, line_items: items });
                        }}
                          className={`px-1.5 py-0.5 rounded text-[10px] border cursor-pointer ${(li.activity_types || []).includes(a) ? 'bg-primary/20 text-primary border-primary/30' : 'bg-muted/50 text-muted-foreground border-border/50'}`}
                        >{a}</button>
                      ))}
                    </div>
                    <Input
                      placeholder="Route keywords for hauling (e.g., Pit to Stockpile, Helms) — comma separated"
                      value={(li.rate_descriptions || []).join(", ")}
                      onChange={(e) => {
                        const items = [...form.line_items];
                        items[idx].rate_descriptions = e.target.value.split(",").map(v => v.trim()).filter(Boolean);
                        setForm({ ...form, line_items: items });
                      }}
                      data-testid={`po-line-item-rate-descriptions-${idx}`}
                      className="bg-background border-border text-[11px]"
                    />
                  </div>
                  <Button type="button" variant="ghost" size="icon" className="h-6 w-6 text-destructive shrink-0" onClick={() => setForm(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }))}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Notes</Label>
              <Input data-testid="po-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-border" />
            </div>
            {tenants.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Portal</Label>
                <Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="po-tenant-select"><SelectValue placeholder="Select portal" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.display_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} data-testid="save-po-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== ACTIVITIES TAB (admin-managed, with Default PO + Used-in POs + Remap) ====================
function ActivitiesTab({ globalAdmin }) {
  const [rows, setRows] = useState([]);
  const [pos, setPos] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [sites, setSites] = useState([]);
  const [orphans, setOrphans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editForm, setEditForm] = useState(null);  // {id, name, code, sort_order, default_po_id} or null
  const [creating, setCreating] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", code: "", sort_order: 0 });
  const [remap, setRemap] = useState({ from_activity: "", to_activity: "", date_from: "", date_to: "", contractor_id: "all", site_id: "all" });
  const [remapPreview, setRemapPreview] = useState(null);
  const [remapBusy, setRemapBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usage, poRows, lookups, orphansRes] = await Promise.all([
        api.get("/admin/activities/usage"),
        api.get("/admin/purchase-orders").catch(() => []),
        api.get("/lookups"),
        api.get("/admin/activities/orphans").catch(() => []),
      ]);
      setRows(usage || []);
      setPos(poRows || []);
      setContractors(lookups.contractors || []);
      setSites(lookups.sites || []);
      setOrphans(orphansRes || []);
    } catch (err) {
      toast.error("Failed to load activities");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveDefaultPo = async (row, composite) => {
    // composite is "po_id" or "po_id::item_code" or ""
    let po_id = "";
    let item_code = "";
    if (composite) {
      const parts = composite.split("::");
      po_id = parts[0] || "";
      item_code = parts[1] || "";
    }
    try {
      // Send only the changed field — keep the activity doc clean
      await api.put(`/admin/activities/${row.id}`, {
        name: row.name,
        code: row.code || "",
        sort_order: row.sort_order ?? 0,
        active: row.active !== false,
        default_po_id: po_id || null,
        default_po_line_item: item_code || null,
      });
      toast.success(`Default PO updated for ${row.name}`);
      await load();
    } catch (err) {
      toast.error("Failed to update default PO");
    }
  };

  // Build dropdown options: each PO contributes one option per line_item, plus a "PO-level" option if no line items.
  const poOptions = [];
  pos.forEach(p => {
    const items = p.line_items || [];
    if (items.length === 0) {
      poOptions.push({ value: p.id, label: `${p.po_number} (PO-level)`, po_number: p.po_number, item_code: "" });
    } else {
      items.forEach(li => {
        poOptions.push({
          value: `${p.id}::${li.item_code || ""}`,
          label: `${p.po_number} / ${li.item_code || "—"} — ${(li.description || "").slice(0, 40)}`,
          po_number: p.po_number,
          item_code: li.item_code || "",
        });
      });
    }
  });

  const saveEdit = async () => {
    if (!editForm) return;
    try {
      await api.put(`/admin/activities/${editForm.id}`, {
        name: editForm.name, code: editForm.code, sort_order: Number(editForm.sort_order) || 0,
      });
      toast.success("Activity updated");
      setEditForm(null);
      await load();
    } catch { toast.error("Failed to save"); }
  };

  const createRow = async () => {
    if (!newRow.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/admin/activities", { ...newRow, sort_order: Number(newRow.sort_order) || 0, active: true });
      toast.success("Activity created");
      setNewRow({ name: "", code: "", sort_order: 0 });
      setCreating(false);
      await load();
    } catch { toast.error("Failed to create"); }
  };

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete activity "${row.name}"? This does NOT delete historical reports — they will keep the activity name as a string.`)) return;
    try {
      await api.delete(`/admin/activities/${row.id}`);
      toast.success("Deleted");
      await load();
    } catch { toast.error("Failed to delete"); }
  };

  const toggleActive = async (row, nextActive) => {
    try {
      await api.put(`/admin/activities/${row.id}`, {
        name: row.name,
        code: row.code || "",
        sort_order: row.sort_order ?? 0,
        active: nextActive,
        default_po_id: row.default_po_id || null,
        default_po_line_item: row.default_po_line_item || null,
      });
      toast.success(`${row.name} ${nextActive ? "activated" : "deactivated"} — operators will ${nextActive ? "see" : "no longer see"} it on new reports.`);
      await load();
    } catch (err) {
      toast.error("Failed to update activity status");
    }
  };

  const runRemap = async (dryRun) => {
    if (!remap.from_activity || !remap.to_activity) {
      toast.error("Pick both From and To activities"); return;
    }
    setRemapBusy(true);
    try {
      const body = { ...remap, dry_run: dryRun };
      if (body.contractor_id === "all") delete body.contractor_id;
      if (body.site_id === "all") delete body.site_id;
      if (!body.date_from) delete body.date_from;
      if (!body.date_to) delete body.date_to;
      const res = await api.post("/admin/activities/remap", body);
      setRemapPreview(res);
      if (!dryRun) {
        toast.success(`Remapped ${res.modified} line(s) — $${res.affected_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
        setRemap({ from_activity: "", to_activity: "", date_from: "", date_to: "", contractor_id: "all", site_id: "all" });
        setRemapPreview(null);
        await load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Remap failed");
    } finally {
      setRemapBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="activities-tab">
      <Card className="border-border bg-card">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold">Activities</h3>
              <p className="text-xs text-muted-foreground">Operators select an activity when logging equipment hours. Admins control which PO each activity rolls into via the Default PO column. The "Used in POs" column shows POs that already reference this activity.</p>
            </div>
            <Button onClick={() => setCreating(true)} size="sm" data-testid="add-activity-btn">
              <Plus className="h-3.5 w-3.5 mr-1" />Add Activity
            </Button>
          </div>
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs">Name</TableHead>
                    <TableHead className="text-xs">Code</TableHead>
                    <TableHead className="text-xs text-right">Sort</TableHead>
                    <TableHead className="text-xs text-right">Lines (history)</TableHead>
                    <TableHead className="text-xs">Default PO</TableHead>
                    <TableHead className="text-xs">Used in POs</TableHead>
                    <TableHead className="text-xs text-center">Active</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {creating && (
                    <TableRow className="border-border bg-emerald-500/5">
                      <TableCell><Input className="h-8 text-xs bg-background border-border" placeholder="Activity name" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} data-testid="new-activity-name" /></TableCell>
                      <TableCell><Input className="h-8 text-xs bg-background border-border" placeholder="CODE" value={newRow.code} onChange={(e) => setNewRow({ ...newRow, code: e.target.value })} /></TableCell>
                      <TableCell><Input type="number" className="h-8 text-xs bg-background border-border w-20" value={newRow.sort_order} onChange={(e) => setNewRow({ ...newRow, sort_order: e.target.value })} /></TableCell>
                      <TableCell />
                      <TableCell colSpan={3} />
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={createRow} className="text-emerald-400" data-testid="save-new-activity"><Check className="h-3 w-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setNewRow({ name: "", code: "", sort_order: 0 }); }}><X className="h-3 w-3" /></Button>
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map(r => {
                    const isEditing = editForm?.id === r.id;
                    return (
                      <TableRow key={r.id} className="border-border" data-testid={`activity-row-${r.id}`}>
                        <TableCell className="text-xs">
                          {isEditing ? <Input className="h-8 text-xs bg-background border-border" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /> : <span className="font-medium">{r.name}</span>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">
                          {isEditing ? <Input className="h-8 text-xs bg-background border-border" value={editForm.code || ""} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} /> : (r.code || "—")}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {isEditing ? <Input type="number" className="h-8 text-xs bg-background border-border w-20" value={editForm.sort_order} onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })} /> : (r.sort_order ?? 0)}
                        </TableCell>
                        <TableCell className="text-xs text-right font-mono text-muted-foreground">{r.line_count || 0}</TableCell>
                        <TableCell>
                          {(() => {
                            const composite = r.default_po_id ? `${r.default_po_id}${r.default_po_line_item ? "::" + r.default_po_line_item : ""}` : "";
                            return (
                              <Select value={composite || "__none__"} onValueChange={(v) => saveDefaultPo(r, v === "__none__" ? "" : v)}>
                                <SelectTrigger className="w-[260px] h-8 bg-background border-border text-xs" data-testid={`default-po-${r.id}`}>
                                  <SelectValue placeholder="— None —">
                                    {r.default_po ? (`${r.default_po.po_number}${r.default_po.line_item ? " / " + r.default_po.line_item : ""}${r.default_po.active === false ? " (inactive)" : ""}`) : "— None —"}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent className="bg-card border-border max-h-[300px]">
                                  <SelectItem value="__none__">— None (auto-match) —</SelectItem>
                                  {poOptions.map(opt => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            );
                          })()}
                          {r.default_po && r.default_po.active === false && (
                            <span className="text-[10px] text-amber-400 ml-1">stale — PO inactive</span>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {(r.used_in_pos || []).length === 0 ? <span className="text-muted-foreground">—</span> : (
                            <div className="flex flex-col gap-1 max-w-[280px]">
                              {r.used_in_pos.map(p => (
                                <div key={p.id} className="flex flex-wrap items-center gap-1">
                                  <Badge variant="outline" className="text-[10px] font-mono">{p.po_number}</Badge>
                                  {(p.line_items || []).map(li => (
                                    <Badge key={li.item_code} variant="outline" className="text-[10px] font-mono bg-primary/10 text-primary border-primary/30" title={li.description}>
                                      {li.item_code}
                                    </Badge>
                                  ))}
                                  {p.covered_at_po_level && (p.line_items || []).length === 0 && (
                                    <span className="text-[10px] text-muted-foreground">PO-level</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center">
                            <Switch
                              checked={r.active !== false}
                              onCheckedChange={(v) => toggleActive(r, v)}
                              data-testid={`activity-active-toggle-${r.id}`}
                              title={r.active !== false ? "Active — operators can see this activity" : "Inactive — hidden from operator dropdowns"}
                            />
                          </div>
                          {r.active === false && <div className="text-[10px] text-muted-foreground mt-0.5">hidden</div>}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          {isEditing ? (
                            <>
                              <Button size="sm" variant="ghost" onClick={saveEdit} className="text-emerald-400 h-7 w-7 p-0"><Check className="h-3 w-3" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditForm(null)} className="h-7 w-7 p-0"><X className="h-3 w-3" /></Button>
                            </>
                          ) : (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => setEditForm({ id: r.id, name: r.name, code: r.code || "", sort_order: r.sort_order ?? 0 })} className="h-7 w-7 p-0" data-testid={`edit-activity-${r.id}`}><Pencil className="h-3 w-3" /></Button>
                              {globalAdmin && <Button size="sm" variant="ghost" onClick={() => deleteRow(r)} className="h-7 w-7 p-0 text-destructive" data-testid={`delete-activity-${r.id}`}><Trash2 className="h-3 w-3" /></Button>}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Orphan Activities — strings used in equipment_hours that aren't registered */}
      {orphans.length > 0 && (
        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="activity-orphans-card">
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" /> Orphan Activities ({orphans.length})
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              These activity names appear on historical equipment hours but are NOT registered in the Activities table — so the resolver can't roll their costs into any PO line item, and they show as "Unassigned" on invoices. Click "Remap to…" to pre-fill the Activity Remap card below; pick the correct registered activity, click <strong>Apply Remap</strong>, and the costs will flow into the right PO/ITM.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-amber-500/30">
                    <TableHead className="text-xs">Orphan Activity</TableHead>
                    <TableHead className="text-xs text-right">Lines</TableHead>
                    <TableHead className="text-xs text-right">Hours</TableHead>
                    <TableHead className="text-xs text-right">Estimated Cost</TableHead>
                    <TableHead className="text-xs text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orphans.map(o => (
                    <TableRow key={o.activity} className="border-amber-500/20" data-testid={`orphan-row-${o.activity}`}>
                      <TableCell className="text-xs font-mono text-amber-400">{o.activity}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{o.line_count}</TableCell>
                      <TableCell className="text-xs text-right font-mono">{o.total_hours.toFixed(1)}h</TableCell>
                      <TableCell className="text-xs text-right font-mono font-bold">${(o.estimated_cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" className="border-amber-500/30 text-xs h-7"
                          onClick={() => { setRemap({ ...remap, from_activity: o.activity }); setRemapPreview(null); toast.info(`Pre-filled — pick a "To" activity below and Preview/Apply.`); }}
                          data-testid={`orphan-remap-${o.activity}`}
                        >Remap to…</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Activity Remap Tool */}
      <Card className="border-border bg-card" data-testid="activity-remap-card">
        <CardContent className="pt-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold">Activity Remap (bulk rename historical reports)</h3>
            <p className="text-xs text-muted-foreground">Rename every equipment-hour line that uses the "From" activity to the "To" activity. Filters are optional. Always click <strong>Preview</strong> first to see how many lines and dollars will be touched.</p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label className="text-xs">From Activity</Label>
              <Select value={remap.from_activity} onValueChange={(v) => { setRemap({ ...remap, from_activity: v }); setRemapPreview(null); }}>
                <SelectTrigger className="w-[220px] h-8 bg-background border-border text-xs" data-testid="remap-from"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {rows.map(r => <SelectItem key={r.id} value={r.name}>{r.name} {r.line_count ? `(${r.line_count} lines)` : ""}</SelectItem>)}
                  {orphans.length > 0 && <div className="px-2 py-1 text-[10px] uppercase text-amber-400 border-t border-border mt-1">Orphans</div>}
                  {orphans.map(o => <SelectItem key={`o-${o.activity}`} value={o.activity}>{o.activity} ⚠ ({o.line_count} lines)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">To Activity</Label>
              <Select value={remap.to_activity} onValueChange={(v) => { setRemap({ ...remap, to_activity: v }); setRemapPreview(null); }}>
                <SelectTrigger className="w-[200px] h-8 bg-background border-border text-xs" data-testid="remap-to"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {rows.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">From Date</Label>
              <Input type="date" className="h-8 w-[140px] text-xs bg-background border-border" value={remap.date_from} onChange={(e) => { setRemap({ ...remap, date_from: e.target.value }); setRemapPreview(null); }} />
            </div>
            <div>
              <Label className="text-xs">To Date</Label>
              <Input type="date" className="h-8 w-[140px] text-xs bg-background border-border" value={remap.date_to} onChange={(e) => { setRemap({ ...remap, date_to: e.target.value }); setRemapPreview(null); }} />
            </div>
            <div>
              <Label className="text-xs">Contractor</Label>
              <Select value={remap.contractor_id} onValueChange={(v) => { setRemap({ ...remap, contractor_id: v }); setRemapPreview(null); }}>
                <SelectTrigger className="w-[180px] h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All</SelectItem>
                  {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Site</Label>
              <Select value={remap.site_id} onValueChange={(v) => { setRemap({ ...remap, site_id: v }); setRemapPreview(null); }}>
                <SelectTrigger className="w-[160px] h-8 bg-background border-border text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All</SelectItem>
                  {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" variant="outline" onClick={() => runRemap(true)} disabled={remapBusy || !remap.from_activity || !remap.to_activity} className="border-border" data-testid="remap-preview-btn">Preview</Button>
            <Button size="sm" onClick={() => runRemap(false)} disabled={remapBusy || !remapPreview || remapPreview.affected_lines === 0} data-testid="remap-apply-btn">Apply Remap</Button>
          </div>
          {remapPreview && (
            <div className="text-xs p-2 rounded border border-amber-500/30 bg-amber-500/5" data-testid="remap-preview">
              Will rename <strong className="text-amber-400">{remapPreview.affected_lines}</strong> equipment-hour line(s) totalling <strong className="text-amber-400">${remapPreview.affected_cost?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong> from <strong>{remap.from_activity}</strong> → <strong>{remap.to_activity}</strong>. Click <strong>Apply Remap</strong> to commit.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PO RECONCILIATION TAB ====================
function PoReconciliationTab() {
  const [lines, setLines] = useState([]);
  const [pos, setPos] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [filters, setFilters] = useState({
    date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
    date_to: new Date().toISOString().slice(0, 10),
    contractor_id: "all",
    status: "unassigned",
  });
  const [totals, setTotals] = useState({ unassigned: 0, assigned: 0 });
  const [loading, setLoading] = useState(false);
  const [pendingChanges, setPendingChanges] = useState({}); // { line_key: po_id }

  const loadContractors = useCallback(async () => {
    try {
      const lookups = await api.get("/lookups");
      setContractors(lookups.contractors || []);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append("date_from", filters.date_from);
      if (filters.date_to) params.append("date_to", filters.date_to);
      if (filters.contractor_id && filters.contractor_id !== "all") params.append("contractor_id", filters.contractor_id);
      if (filters.status) params.append("status", filters.status);
      const res = await api.get(`/admin/po-reconciliation?${params.toString()}`);
      setLines(res.lines || []);
      setPos(res.purchase_orders || []);
      setTotals({ unassigned: res.total_unassigned || 0, assigned: res.total_assigned || 0 });
      setPendingChanges({});
    } catch (err) {
      toast.error("Failed to load reconciliation data");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadContractors(); }, [loadContractors]);
  useEffect(() => { load(); }, [load]);

  const lineKey = (l) => `${l.collection}:${l.id}`;
  // staged values are composite "po_id::item_code" or "po_id" (PO-level) or "" (clear)
  const stagedPo = (l) => {
    const k = lineKey(l);
    if (k in pendingChanges) return pendingChanges[k];
    const poId = l.po_override_id || l.matched_po_id || "";
    if (!poId) return "";
    const itemCode = l.po_override_line_item || l.matched_line_item || "";
    return itemCode ? `${poId}::${itemCode}` : poId;
  };

  const stageAssignment = (line, composite) => {
    setPendingChanges(prev => ({ ...prev, [lineKey(line)]: composite }));
  };

  const applyChanges = async () => {
    const assignments = Object.entries(pendingChanges).map(([k, composite]) => {
      const [collection, line_id] = k.split(":");
      let po_id = "";
      let line_item = "";
      if (composite) {
        const parts = composite.split("::");
        po_id = parts[0] || "";
        line_item = parts[1] || "";
      }
      return { collection, line_id, po_id, line_item };
    });
    if (!assignments.length) {
      toast.info("No changes to apply");
      return;
    }
    try {
      const res = await api.post("/admin/po-reconciliation/assign", { assignments });
      toast.success(`Updated ${res.updated} of ${res.total_requested} line(s)`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply assignments");
    }
  };

  const bulkAssign = (composite) => {
    if (!lines.length) return;
    const next = {};
    for (const l of lines) next[lineKey(l)] = composite;
    setPendingChanges(prev => ({ ...prev, ...next }));
    const label = composite ? composite.replace("::", " / ").substring(0, 30) : "Unassigned";
    toast.info(`Staged ${lines.length} line(s) → ${label}. Click "Apply Changes" to save.`);
  };

  // Build full PO×line_item options once
  const poItemOptions = [];
  pos.forEach(p => {
    const items = p.line_items || [];
    if (items.length === 0) {
      poItemOptions.push({ value: p.id, label: `${p.po_number} (PO-level)` });
    } else {
      items.forEach(li => {
        poItemOptions.push({
          value: `${p.id}::${li.item_code || ""}`,
          label: `${p.po_number} / ${li.item_code || "—"} — ${(li.description || "").slice(0, 30)}`,
        });
      });
    }
  });

  const dirtyCount = Object.keys(pendingChanges).length;

  const applyMaterialDefaults = async () => {
    try {
      const check = await api.post("/admin/materials/apply-default-pos", { dry_run: true });
      if (!check.cleared) { toast.info("All material lines already bill to their material's Default PO"); return; }
      const names = check.materials.map(m => `${m.material_name}: ${m.lines}`).join(", ");
      if (!window.confirm(`${check.cleared} material line(s) are manually pinned to a PO that differs from the material's Default PO (${names}).\n\nClear those pins so they bill to the material's PO?`)) return;
      const res = await api.post("/admin/materials/apply-default-pos", {});
      toast.success(`${res.cleared} line(s) re-pointed to their material's Default PO`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply material defaults");
    }
  };

  return (
    <div className="space-y-4" data-testid="po-reconciliation-tab">
      <Card className="border-border bg-card">
        <CardContent className="pt-4 space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                className="bg-background border-border w-[150px] text-sm" data-testid="recon-date-from" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                className="bg-background border-border w-[150px] text-sm" data-testid="recon-date-to" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Contractor</Label>
              <Select value={filters.contractor_id} onValueChange={(v) => setFilters({ ...filters, contractor_id: v })}>
                <SelectTrigger className="w-[200px] bg-background border-border text-sm" data-testid="recon-contractor"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All Contractors</SelectItem>
                  {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                <SelectTrigger className="w-[160px] bg-background border-border text-sm" data-testid="recon-status"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="unassigned">Unassigned only</SelectItem>
                  <SelectItem value="assigned">Assigned only</SelectItem>
                  <SelectItem value="all">All lines</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={load} variant="outline" size="sm" className="border-border" data-testid="recon-refresh">
              <Filter className="h-3.5 w-3.5 mr-1" />Refresh
            </Button>
            <Button onClick={applyMaterialDefaults} variant="outline" size="sm" className="border-primary/40 text-primary ml-auto" title="Clear manual PO pins on material lines that conflict with the material's Default PO" data-testid="recon-apply-material-defaults">
              Re-point materials to their Default PO
            </Button>
          </div>
          <div className="flex flex-wrap gap-3 text-sm pt-2">
            <span className="text-muted-foreground">Unassigned:</span>
            <span className="font-mono font-bold text-amber-400" data-testid="recon-total-unassigned">${totals.unassigned.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            <span className="text-muted-foreground">Assigned:</span>
            <span className="font-mono font-bold text-emerald-400" data-testid="recon-total-assigned">${totals.assigned.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
            <span className="text-muted-foreground ml-auto">Lines: {lines.length}</span>
          </div>
        </CardContent>
      </Card>

      {pos.length > 0 && lines.length > 0 && (
        <Card className="border-border bg-card">
          <CardContent className="pt-4 flex flex-wrap gap-2 items-center">
            <span className="text-xs text-muted-foreground mr-2">Bulk assign all visible lines to:</span>
            {pos.map(p => (
              <Button key={p.id} variant="outline" size="sm" className="border-border text-xs"
                onClick={() => bulkAssign(p.id)} data-testid={`recon-bulk-${p.po_number}`}>
                {p.po_number}
              </Button>
            ))}
            <Button variant="outline" size="sm" className="border-border text-xs text-muted-foreground"
              onClick={() => bulkAssign("")} data-testid="recon-bulk-clear">Clear all</Button>
          </CardContent>
        </Card>
      )}

      <Card className="border-border bg-card">
        <CardContent className="pt-4">
          {loading ? (
            <div className="py-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : lines.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">
              No lines for the current filter. {filters.status === "unassigned" && totals.assigned > 0 && "All costs are matched to a PO."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead className="text-xs">Date</TableHead>
                    <TableHead className="text-xs">Contractor</TableHead>
                    <TableHead className="text-xs">Site</TableHead>
                    <TableHead className="text-xs">Kind</TableHead>
                    <TableHead className="text-xs">Unit</TableHead>
                    <TableHead className="text-xs">Activity / Route</TableHead>
                    <TableHead className="text-xs text-right">Qty</TableHead>
                    <TableHead className="text-xs text-right">Rate</TableHead>
                    <TableHead className="text-xs text-right">Cost</TableHead>
                    <TableHead className="text-xs">Assign to PO</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((l) => {
                    const k = lineKey(l);
                    const isDirty = k in pendingChanges;
                    const sel = stagedPo(l);
                    return (
                      <TableRow key={k} className={`border-border ${isDirty ? "bg-amber-500/5" : ""}`} data-testid={`recon-row-${k}`}>
                        <TableCell className="text-xs font-mono">{l.report_date}</TableCell>
                        <TableCell className="text-xs">{l.contractor_name}</TableCell>
                        <TableCell className="text-xs">{l.site_name}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline" className="text-[10px]">{l.kind}</Badge>
                        </TableCell>
                        <TableCell className="text-xs font-mono">{l.unit_id || "—"}</TableCell>
                        <TableCell className="text-xs">{l.rate_description || l.activity || "—"}</TableCell>
                        <TableCell className="text-xs text-right font-mono">{Number(l.qty).toFixed(1)} {l.qty_unit}</TableCell>
                        <TableCell className="text-xs text-right font-mono">${Number(l.rate).toFixed(2)}</TableCell>
                        <TableCell className="text-xs text-right font-mono font-bold">${Number(l.cost).toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell>
                          <Select value={sel || "__none__"} onValueChange={(v) => stageAssignment(l, v === "__none__" ? "" : v)}>
                            <SelectTrigger className="w-[260px] bg-background border-border text-xs h-8" data-testid={`recon-select-${k}`}>
                              <SelectValue placeholder="Auto-match">
                                {(() => {
                                  const v = sel;
                                  if (!v) return "— Auto / No PO —";
                                  const opt = poItemOptions.find(o => o.value === v);
                                  return opt ? opt.label : v;
                                })()}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border max-h-[300px]">
                              <SelectItem value="__none__">— Auto / No PO —</SelectItem>
                              {poItemOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {l.matched_line_item && !pendingChanges[k] && (
                            <span className="text-[10px] text-muted-foreground ml-1">→ {l.matched_line_item}</span>
                          )}
                          {l.is_manual_override && <span className="text-[10px] text-amber-400 ml-1">override</span>}
                          {!l.matched_po_id && !pendingChanges[k] && (
                            <details className="mt-1 text-left" data-testid={`recon-why-${l.id}`}>
                              <summary className="text-[10px] text-amber-400 cursor-pointer select-none">Why unassigned?</summary>
                              <div className="mt-1 p-2 rounded bg-amber-500/5 border border-amber-500/20 text-[10px] text-muted-foreground space-y-1 max-w-[360px]">
                                {l.kind === "material" && (
                                  <div>
                                    Material <span className="text-foreground">{l.material_name || "—"}</span>:{" "}
                                    {l.material_default_po ? <>Default PO <span className="font-mono text-foreground">{l.material_default_po}</span> rejected (see below)</> : <span className="text-amber-400">no Default PO set (Admin → Materials)</span>}
                                  </div>
                                )}
                                {(l.why_unassigned || []).length === 0 && <div>No purchase orders exist for this tenant.</div>}
                                {(l.why_unassigned || []).map((w, i) => (
                                  <div key={i}><span className="font-mono text-foreground">{w.po_number}</span>: {w.reason}</div>
                                ))}
                              </div>
                            </details>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2 sticky bottom-2 z-10">
        {dirtyCount > 0 && (
          <span className="text-xs text-amber-400" data-testid="recon-dirty-count">{dirtyCount} change(s) pending</span>
        )}
        <Button variant="outline" size="sm" className="border-border" onClick={() => setPendingChanges({})} disabled={dirtyCount === 0} data-testid="recon-discard-btn">
          Discard
        </Button>
        <Button size="sm" onClick={applyChanges} disabled={dirtyCount === 0} data-testid="recon-apply-btn">
          Apply {dirtyCount > 0 ? `(${dirtyCount})` : ""} Changes
        </Button>
      </div>
    </div>
  );
}

// ==================== REPORT MANAGEMENT TAB ====================
function ReportManagementTab() {
  const [reports, setReports] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filters, setFilters] = useState({ date_from: "", date_to: "", status: "", contractor_id: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [lookups, tenantData] = await Promise.all([
          api.get("/lookups"),
          api.get("/admin/tenants").catch(() => []),
        ]);
        setContractors(lookups.contractors || []);
        setTenants(tenantData || []);
      } catch {}
    };
    loadLookups();
  }, []);

  const search = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.status) params.set("status", filters.status);
      if (filters.contractor_id) params.set("contractor_id", filters.contractor_id);
      const data = await api.get(`/admin/reports?${params.toString()}`);
      setReports(data || []);
    } catch (err) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const deleteReport = async (reportId, date) => {
    if (!window.confirm(`Delete report for ${date}? This will permanently remove all equipment hours, delays, and material movements for this report.`)) return;
    try {
      await api.delete(`/admin/reports/${reportId}`);
      toast.success("Report deleted");
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (err) {
      toast.error("Failed to delete report");
    }
  };

  const changeStatus = async (reportId, newStatus) => {
    try {
      await api.put(`/admin/reports/${reportId}/status`, { status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      setReports(prev => prev.map(r => r.id === reportId ? { ...r, status: newStatus } : r));
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const getContractorName = (id) => contractors.find(c => c.id === id)?.name || "—";
  const getTenantName = (id) => tenants.find(t => t.id === id)?.display_name || "—";

  const statusColors = {
    Draft: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    Saved: "bg-blue-500/20 text-blue-400 border-blue-500/30",
    Submitted: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
    Approved: "bg-primary/20 text-primary border-primary/30",
    Rejected: "bg-red-500/20 text-red-400 border-red-500/30",
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">From</Label>
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="bg-background border-border w-40 text-sm" data-testid="report-mgmt-date-from" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">To</Label>
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="bg-background border-border w-40 text-sm" data-testid="report-mgmt-date-to" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-background border-border w-36 text-sm" data-testid="report-mgmt-status"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Saved">Saved</SelectItem>
              <SelectItem value="Submitted">Submitted</SelectItem>
              <SelectItem value="Approved">Approved</SelectItem>
              <SelectItem value="Rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Contractor</Label>
          <Select value={filters.contractor_id} onValueChange={(v) => setFilters({ ...filters, contractor_id: v === "all" ? "" : v })}>
            <SelectTrigger className="bg-background border-border w-48 text-sm" data-testid="report-mgmt-contractor"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All Contractors</SelectItem>
              {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={search} disabled={loading} data-testid="report-mgmt-search">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}
        </Button>
      </div>

      {reports.length > 0 && (
        <p className="text-sm text-muted-foreground">{reports.length} report(s) found</p>
      )}

      <Card className="border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-xs uppercase">Date</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Shift</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Portal</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Status</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Created</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Use the filters above to search for reports. You can manage stuck, orphan, or incorrect reports here.</TableCell></TableRow>
            ) : (
              reports.map((r) => (
                <TableRow key={r.id} className="border-border">
                  <TableCell className="font-mono text-sm font-medium">{r.report_date}</TableCell>
                  <TableCell className="text-sm">{r.shift}</TableCell>
                  <TableCell className="text-sm">{getContractorName(r.contractor_id)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{getTenantName(r.tenant_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${statusColors[r.status] || "bg-muted text-muted-foreground border-border"}`}>{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Select onValueChange={(v) => changeStatus(r.id, v)}>
                        <SelectTrigger className="h-7 w-24 bg-background border-border text-xs" data-testid={`report-status-${r.id}`}><SelectValue placeholder="Status" /></SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="Draft">Draft</SelectItem>
                          <SelectItem value="Saved">Saved</SelectItem>
                          <SelectItem value="Submitted">Submitted</SelectItem>
                          <SelectItem value="Approved">Approved</SelectItem>
                          <SelectItem value="Rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteReport(r.id, r.report_date)} data-testid={`delete-report-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}

// ==================== RATE CARDS TAB ====================
function RateCardsTab() {
  const [rates, setRates] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [pos, setPos] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedContractor, setSelectedContractor] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ contractor_id: "", category: "", description: "", rate: "", unit: "", tenant_ids: [], default_po_id: "", material_id: "" });
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    try {
      const [ratesData, lookups, tenantData, poData] = await Promise.all([
        api.get("/admin/contractor-rates"),
        api.get("/lookups"),
        api.get("/admin/tenants").catch(() => []),
        api.get("/admin/purchase-orders").catch(() => []),
      ]);
      setRates(ratesData || []);
      setContractors(lookups.contractors || []);
      setMaterials(lookups.materials || []);
      setSites(lookups.sites || []);
      setTenants(tenantData || []);
      setPos(poData || []);
    } catch (err) {
      toast.error("Failed to load rate data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const filteredRates = selectedContractor && selectedContractor !== "all" ? rates.filter(r => r.contractor_id === selectedContractor) : rates;
  const getContractorName = (id) => contractors.find(c => c.id === id)?.name || "Unknown";
  const MATERIAL_CATEGORIES = ["Hauling", "Stripping"];
  const materialLabel = (m) => {
    const site = sites.find(s => s.id === m.site_id);
    return `${m.name}${site ? ` (${site.name})` : ""}`;
  };
  const materialPo = (m) => pos.find(p => p.id === m?.default_po_id);
  const getMaterialCell = (r) => {
    if (!r.material_id) return null;
    const m = materials.find(x => x.id === r.material_id);
    if (!m) return { label: "Unknown material", po: null };
    return { label: materialLabel(m), po: materialPo(m) };
  };

  const CATEGORIES = ["Equipment", "Hauling", "Stripping", "Labor", "Supervision"];
  const UNITS = ["Hour", "Ton", "CYD", "Load", "Each"];

  const openAdd = () => {
    setEditItem(null);
    setForm({ contractor_id: selectedContractor && selectedContractor !== "all" ? selectedContractor : "", category: "", description: "", rate: "", unit: "", tenant_ids: [], default_po_id: "", material_id: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      contractor_id: item.contractor_id || "",
      category: item.category || "",
      description: item.description || "",
      rate: String(item.rate || ""),
      unit: item.unit || "",
      tenant_ids: item.tenant_ids || [],
      default_po_id: item.default_po_id || "",
      material_id: item.material_id || "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.contractor_id || !form.category || !form.description || !form.rate || !form.unit) {
      toast.error("All fields are required");
      return;
    }
    try {
      const isMaterialRate = MATERIAL_CATEGORIES.includes(form.category);
      const payload = { ...form, rate: parseFloat(form.rate), default_po_id: form.default_po_id || null, material_id: isMaterialRate ? (form.material_id || null) : null };
      if (editItem) {
        await api.put(`/admin/contractor-rates/${editItem.id}`, payload);
        toast.success("Rate updated");
      } else {
        await api.post("/admin/contractor-rates", payload);
        toast.success("Rate added");
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save rate");
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/admin/contractor-rates/${id}`);
      toast.success("Rate deleted");
      await loadData();
    } catch (err) {
      toast.error("Failed to delete rate");
    }
  };

  const toggleTenant = (tid) => {
    setForm(prev => ({
      ...prev,
      tenant_ids: prev.tenant_ids.includes(tid)
        ? prev.tenant_ids.filter(id => id !== tid)
        : [...prev.tenant_ids, tid],
    }));
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading rates...</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select value={selectedContractor} onValueChange={setSelectedContractor}>
            <SelectTrigger className="bg-background border-border w-56" data-testid="rate-contractor-filter">
              <SelectValue placeholder="All Contractors" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="all">All Contractors</SelectItem>
              {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-muted-foreground">{filteredRates.length} rate(s)</span>
        </div>
        <Button size="sm" onClick={openAdd} data-testid="add-rate-button">
          <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Add Rate
        </Button>
      </div>

      <Card className="border-border">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead className="text-muted-foreground text-xs uppercase">Contractor</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Category</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Description</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Rate</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Unit</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase">Material → PO</TableHead>
              <TableHead className="text-muted-foreground text-xs uppercase text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRates.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No rates configured. Click "Add Rate" to create the contractor's rate card.</TableCell></TableRow>
            ) : (
              filteredRates.map((r) => (
                <TableRow key={r.id} className="border-border">
                  <TableCell className="font-medium text-sm">{getContractorName(r.contractor_id)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${
                      r.category === "Hauling" ? "bg-blue-500/20 text-blue-400 border-blue-500/30" :
                      r.category === "Stripping" ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                      r.category === "Equipment" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                      "bg-muted text-muted-foreground border-border"
                    }`}>{r.category}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{r.description}</TableCell>
                  <TableCell className="text-right font-mono text-sm">${parseFloat(r.rate).toFixed(2)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">/{r.unit}</TableCell>
                  <TableCell className="text-xs" data-testid={`rate-material-${r.id}`}>
                    {(() => {
                      if (!MATERIAL_CATEGORIES.includes(r.category)) return <span className="text-muted-foreground">— (billed by activity)</span>;
                      const cell = getMaterialCell(r);
                      if (!cell) return <span className="text-amber-400">No material tied — operators can't use this rate</span>;
                      return (
                        <div className="flex flex-col">
                          <span className="font-medium">{cell.label}</span>
                          <span className="text-[10px] text-muted-foreground font-mono">{cell.po ? `→ ${cell.po.po_number}` : "→ no Default PO on material (auto-match)"}</span>
                        </div>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`edit-rate-${r.id}`}>
                        <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(r.id)} data-testid={`delete-rate-${r.id}`}>
                        <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-headline">{editItem ? "Edit" : "Add"} Rate Entry</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Define a rate from the contractor's rate card</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Contractor *</Label>
              <Select value={form.contractor_id} onValueChange={(v) => setForm({ ...form, contractor_id: v })}>
                <SelectTrigger className="bg-background border-border" data-testid="rate-contractor-select"><SelectValue placeholder="Select contractor" /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="rate-category-select"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Unit *</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="rate-unit-select"><SelectValue placeholder="Unit" /></SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Description *</Label>
              <Input data-testid="rate-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g., Helms (King Ranch III), Stripping - 730 + 336" className="bg-background border-border" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Rate ($) *</Label>
              <Input data-testid="rate-amount-input" type="number" step="0.01" min="0" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} placeholder="0.00" className="bg-background border-border font-mono" />
            </div>
            {tenants.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Assigned Portals</Label>
                <div className="flex flex-wrap gap-2">
                  {tenants.map(t => (
                    <button key={t.id} type="button" onClick={() => toggleTenant(t.id)}
                      className={`px-2.5 py-1 rounded text-xs border transition-colors cursor-pointer ${form.tenant_ids.includes(t.id) ? 'bg-primary/20 text-primary border-primary/30' : 'bg-muted text-muted-foreground border-border hover:border-primary/30'}`}
                    >{t.display_name}</button>
                  ))}
                </div>
              </div>
            )}
            {MATERIAL_CATEGORIES.includes(form.category) && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Material (ties this rate to a material)</Label>
                <Select value={form.material_id || "__none__"} onValueChange={(v) => setForm({ ...form, material_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="bg-background border-border" data-testid="rate-material-select"><SelectValue placeholder="Select material" /></SelectTrigger>
                  <SelectContent className="bg-card border-border max-h-72">
                    <SelectItem value="__none__">— Not tied to a material —</SelectItem>
                    {materials.map(m => <SelectItem key={m.id} value={m.id}>{materialLabel(m)}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(() => {
                  const m = materials.find(x => x.id === form.material_id);
                  const po = materialPo(m);
                  return (
                    <p className="text-[11px] text-muted-foreground">
                      {m ? (po ? `Bills to ${po.po_number}${po.description ? ` — ${po.description}` : ""} (the material's Default PO).` : "This material has no Default PO yet — set it in Admin → Materials.") : "When the operator picks this material, this route auto-fills (locked) if it is the only one; with several routes the operator picks one. The cost bills to the material's Default PO."}
                    </p>
                  );
                })()}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-border">Cancel</Button>
            <Button onClick={handleSave} data-testid="save-rate-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const { navigate } = useTenantNavigation();
  const globalAdmin = user?.role === "admin" && !user?.tenant_id;
  const [activeTab, setActiveTab] = useState("sites");

  return (
    <div className="min-h-screen" data-testid="admin-page">
      <header className="border-b border-border px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo />
          <Badge variant="outline" className="bg-primary/20 text-primary border-primary/30 text-xs ml-2">{globalAdmin ? "Global Admin" : "Tenant Admin"}</Badge>
        </div>
        <div className="flex items-center gap-4">
          <NotificationBell />
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.name || user?.email}</span>
          <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </Button>
        </div>
      </header>

      <main className={`${["invoices", "rate-cards", "po-reconciliation", "purchase-orders"].includes(activeTab) ? "max-w-7xl" : "max-w-4xl"} mx-auto px-4 py-8 transition-[max-width] duration-300`}>
        <h1 className="font-headline text-3xl font-bold tracking-tight mb-2">Admin Panel</h1>
        <div className="flex items-center justify-between mb-8">
          <p className="text-muted-foreground text-sm">Manage lookup data and system users</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/reports")} data-testid="view-reports-button">
              <FileText className="h-4 w-4 mr-1" strokeWidth={1.5} /> View Reports
            </Button>
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/consolidated")} data-testid="consolidated-report-button">
              <BarChart3 className="h-4 w-4 mr-1" strokeWidth={1.5} /> Consolidated Report
            </Button>
            <Button variant="outline" size="sm" className="border-border" onClick={() => navigate("/analytics")} data-testid="analytics-button">
              <BarChart3 className="h-4 w-4 mr-1" strokeWidth={1.5} /> Analytics Dashboard
            </Button>
          </div>
        </div>

        <Tabs defaultValue="sites" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-card border border-border mb-6 flex-wrap h-auto gap-1 p-1">
            {Object.entries(TAB_CONFIG).map(([key, cfg]) => (
              <TabsTrigger key={key} value={key} className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid={`admin-tab-${key}`}>
                <cfg.icon className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />{cfg.label}
              </TabsTrigger>
            ))}
            <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-users">
              <Users className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Users
            </TabsTrigger>
            <TabsTrigger value="notifications" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-notifications">
              <Bell className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Notifications
            </TabsTrigger>
            <TabsTrigger value="powerbi" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-powerbi">
              <Key className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Power BI
            </TabsTrigger>
            {globalAdmin && (
              <TabsTrigger value="tenants" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-tenants">
                <Building2 className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Tenants
              </TabsTrigger>
            )}
            <TabsTrigger value="invoices" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-invoices">
              <Receipt className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Invoices
            </TabsTrigger>
            <TabsTrigger value="rate-cards" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-rate-cards">
              <DollarSign className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Rate Cards
            </TabsTrigger>
            <TabsTrigger value="report-mgmt" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-report-mgmt">
              <FileText className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Reports
            </TabsTrigger>
            <TabsTrigger value="purchase-orders" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-purchase-orders">
              <Receipt className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />Purchase Orders
            </TabsTrigger>
            <TabsTrigger value="po-reconciliation" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-xs" data-testid="admin-tab-po-reconciliation">
              <Receipt className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />PO Reconciliation
            </TabsTrigger>
          </TabsList>

          {Object.entries(TAB_CONFIG).map(([key, cfg]) => (
            key === "activities" ? null : (
              <TabsContent key={key} value={key}>
                <LookupTab config={cfg} globalAdmin={globalAdmin} />
              </TabsContent>
            )
          ))}
          <TabsContent value="activities">
            <ActivitiesTab globalAdmin={globalAdmin} />
          </TabsContent>
          <TabsContent value="users">
            <UsersTab globalAdmin={globalAdmin} />
          </TabsContent>
          <TabsContent value="notifications">
            <NotificationsTab />
          </TabsContent>
          <TabsContent value="powerbi">
            <PowerBITab />
          </TabsContent>
          <TabsContent value="tenants">
            <TenantsTab />
          </TabsContent>
          <TabsContent value="invoices">
            <InvoiceTab />
          </TabsContent>
          <TabsContent value="rate-cards">
            <RateCardsTab />
          </TabsContent>
          <TabsContent value="report-mgmt">
            <ReportManagementTab />
          </TabsContent>
          <TabsContent value="purchase-orders">
            <PurchaseOrdersTab />
          </TabsContent>
          <TabsContent value="po-reconciliation">
            <PoReconciliationTab />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
